/* sts-liquid-quiz-results.js — RESULTS engine for the liquid-proof-of-concept quiz.
 *
 * Exposed as window.SLQ.results (vanilla ES2019, classic script, no build step).
 * Entry point (called by the quiz engine / section script once a submit outcome
 * carries a widgetToken):
 *
 *   window.SLQ.results.init({
 *     ec2Base:     'https://api-dev.skintype.pro',   // no trailing slash needed
 *     widgetToken: '<JWT from the submit outcome>',
 *     skinType:    'OSPT',                            // 4-letter Baumann code
 *     root:        optional Element (defaults to [data-slq-results])
 *   });
 *
 * Data: GET {ec2Base}/widget/regimen with Authorization: Bearer <widgetToken>
 * (spec §1.3). Pure step helpers are FAITHFUL PORTS of the headless widget's
 * regimen.ts (isAddableStep/isInformationalStep/isBundleAddableStep/
 * filterStepIndicesByTier/computeAddableTotal/applySwap) and
 * results/fwc/step-groups.ts (buildStepGroups/dedupeByVariant) — same
 * semantics, same edge cases, vanilla syntax.
 *
 * Per-type theming reads window.__STS_SKIN_TYPE_INFO__[code] = {color, slug,
 * videoUrl} (injected by the section from shop.metafields.global.skinTypeInfo).
 * Telemetry goes through window.SLQ.track(event, detail) when the quiz engine
 * provides it; absent tracker == silent no-op (never fabricated).
 *
 * No secrets, no Lambda/API-Gateway URLs — the only remote host is the
 * section-configured EC2 base; carts and selling plans are same-origin.
 */
(function () {
  'use strict';

  var NS = (window.SLQ = window.SLQ || {});

  /* ------------------------------------------------------------------ */
  /* telemetry                                                           */
  /* ------------------------------------------------------------------ */

  /* On the quiz page the quiz engine owns SLQ.track. On the STANDALONE results
   * page it is not loaded, so without a fallback every results_viewed and
   * add_to_cart would silently vanish — the failure mode telemetry is worst at,
   * because nothing looks broken.
   *
   * The fallback deliberately reuses the SAME sessionStorage keys the quiz
   * engine uses (`slq_session_uuid`, `slq_client_seq`), so events fired after
   * the redirect still join the visitor's quiz session instead of opening an
   * orphan one. `page_context` is 'results-page-liquid' — the events really do
   * come from a different page and are labelled honestly, never disguised as
   * quiz-page events. */
  var WIDGET_VERSION = 'liquid-poc-1';
  var PAGE_CONTEXT = 'results-page-liquid';
  var ASSIGNMENTS = { concept: 'liquid-poc' };

  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } }

  function trackUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
  }

  function trackSessionUuid() {
    var existing = ssGet('slq_session_uuid');
    if (existing) return existing;
    var fresh = trackUuid();
    ssSet('slq_session_uuid', fresh);
    return fresh;
  }

  function trackNextSeq() {
    var raw = parseInt(ssGet('slq_client_seq') || '0', 10);
    if (!isFinite(raw) || raw < 0) raw = 0;
    var next = raw + 1;
    ssSet('slq_client_seq', String(next));
    return next;
  }

  function trackStandalone(event, detail) {
    try {
      var base = String((S && S.ec2Base) || '').replace(/\/+$/, '');
      if (!base) return;
      var evt = {
        event_uuid: trackUuid(),
        session_uuid: trackSessionUuid(),
        client_seq: trackNextSeq(),
        occurred_at: new Date().toISOString(),
        event: String(event),
        widget_version: WIDGET_VERSION,
        page_context: PAGE_CONTEXT,
        assignments: ASSIGNMENTS,
        device_class: window.innerWidth <= 760 ? 'mobile' : 'desktop'
      };
      if (detail && typeof detail === 'object') evt.detail = detail;
      var envelope = { events: [evt] };
      if (S && S.token) envelope.token = S.token;
      var url = base + '/widget/events';
      var payload = JSON.stringify(envelope);
      // Beacon FIRST: add_to_cart fires immediately before the cart navigation
      // and fetch+keepalive only sometimes survives that unload.
      try {
        if (navigator.sendBeacon) {
          var blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
          if (navigator.sendBeacon(url, blob)) return;
        }
      } catch (e) { /* fall through to fetch */ }
      window.fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(function () { /* telemetry is fire-and-forget */ });
    } catch (e) { /* never break the results for telemetry */ }
  }

  function track(event, detail) {
    if (typeof NS.track === 'function') {
      try { NS.track(event, detail); } catch (e) { /* telemetry must never break UI */ }
      return;
    }
    trackStandalone(event, detail);
  }

  /* ------------------------------------------------------------------ */
  /* pure step helpers — ported verbatim from widget/src/regimen.ts      */
  /* ------------------------------------------------------------------ */

  function isAddableStep(step) {
    return !!step.dtcVariantId && !!step.title &&
      step.priceMsrp !== null && step.priceMsrp !== undefined;
  }

  function isInformationalStep(step) {
    return !step.dtcVariantId || !step.title;
  }

  function isBundleAddableStep(step) {
    return isAddableStep(step) && step.inStock !== false;
  }

  function filterStepIndicesByTier(steps, tier) {
    var indices = [];
    steps.forEach(function (step, index) {
      if (tier === 'complete' || step.tier === 'starter') indices.push(index);
    });
    return indices;
  }

  function computeAddableTotal(steps) {
    return steps.reduce(function (sum, step) {
      return isBundleAddableStep(step) ? sum + step.priceMsrp : sum;
    }, 0);
  }

  /* An alternative the shopper can actually buy. The stock check is NOT
   * cosmetic: without it the swap drawer offers products that fail at
   * add-to-cart, and the out-of-stock substitution below would "fix" an
   * unbuyable step by choosing another unbuyable one. */
  function isValidSwapCandidate(candidate) {
    return !!candidate && !!candidate.dtcVariantId && !!candidate.title &&
      candidate.inStock !== false;
  }

  /* The fields a candidate contributes when it takes a step's place. Shared by
   * applySwap (shopper chose it) and substituteOutOfStock (we chose it for
   * them) so the two can never drift. */
  function stepFieldsFromCandidate(candidate) {
    return {
      title: candidate.title,
      brand: candidate.brand,
      image: candidate.image,
      priceMsrp: candidate.priceMsrp,
      dtcVariantId: candidate.dtcVariantId,
      inStock: candidate.inStock,
      handle: candidate.handle,
      keyIngredients: candidate.keyIngredients || [],
      ingredientsSummary: null,
      url: null
    };
  }

  /* Pure: returns a NEW array; keeps slot/tier/time/swapCandidates; clears
   * ingredientsSummary/url (no candidate equivalent). No-op on invalid input. */
  function applySwap(steps, index, candidate) {
    if (index < 0 || index >= steps.length || !isValidSwapCandidate(candidate)) return steps;
    return steps.map(function (step, i) {
      if (i !== index) return step;
      return Object.assign({}, step, stepFieldsFromCandidate(candidate));
    });
  }

  /* Within-slot out-of-stock substitution.
   *
   * The recommendation engine can return a step whose product is out of stock.
   * Left alone it renders as a product the shopper cannot buy: it is excluded
   * from the total and from add-to-cart (isBundleAddableStep), so the routine
   * silently loses a step and the shopper is never told why. The live quiz has
   * always substituted the next available product in the same slot
   * (quiz-results.js: preselectNextProductIfCurrentProductIsNotAvailableOrOutOfStock).
   *
   * The card's "In-stock substitute" badge and the results_viewed
   * `substitutedCount` metric were both already built for this — only the
   * substitution itself was missing.
   *
   * Every candidate is already slot-matched and dermatologist-approved for the
   * skin type, so promoting one is safe. If nothing in the slot is in stock we
   * leave the step alone rather than inventing a replacement. */
  function substituteOutOfStock(step) {
    if (!step || step.inStock !== false || step.substituted === true) return step;
    var replacement = null;
    for (var i = 0; i < (step.swapCandidates || []).length; i++) {
      if (isValidSwapCandidate(step.swapCandidates[i])) { replacement = step.swapCandidates[i]; break; }
    }
    if (!replacement) return step;
    return Object.assign({}, step, stepFieldsFromCandidate(replacement), {
      substituted: true,
      substitutedFrom: step.title || null
    });
  }

  function applySwaps(steps, swaps) {
    return Object.keys(swaps).reduce(function (acc, indexKey) {
      return applySwap(acc, Number(indexKey), swaps[indexKey]);
    }, steps);
  }

  /* ------------------------------------------------------------------ */
  /* step groups — ported from widget/src/results/fwc/step-groups.ts     */
  /* ------------------------------------------------------------------ */

  function combineTime(times) {
    var hasBoth = times.some(function (t) { return t === 'BOTH'; });
    if (hasBoth) return 'BOTH';
    var hasAm = times.some(function (t) { return t === 'AM'; });
    var hasPm = times.some(function (t) { return t === 'PM'; });
    if (hasAm && hasPm) return 'BOTH';
    if (hasAm) return 'AM';
    if (hasPm) return 'PM';
    return null;
  }

  /* One product = one element. The same dtcVariantId in two slots of the SAME
   * category (the AM cleanser slot and the PM cleanser slot both holding the
   * Zerafite cleanser, e.g. after "Use here too") collapses into ONE group
   * tagged AM + PM, exactly like a twice-daily product in a single slot (Paul,
   * 2026-08-19: the glance list and the top summary showed it twice and the
   * PM card duplicated the AM card). Different categories never merge - a
   * product doing double duty as treatment AND moisturizer is two steps.
   * Unresolved steps (no variant) keep the old slot::null key; informational
   * rows never merge (stable per-index key). First-appearance order. */
  function groupKeyFor(step, index) {
    if (isInformationalStep(step)) return 'info:' + index;
    var slot = String(step.slot === null || step.slot === undefined ? '' : step.slot);
    var cat = step.dtcVariantId ? categoryForSlot(slot) : null;
    return (cat ? 'cat:' + cat : slot) + '::' + step.dtcVariantId;
  }

  function buildStepGroups(steps) {
    var order = [];
    var byKey = new Map();
    steps.forEach(function (step, index) {
      var key = groupKeyFor(step, index);
      var existing = byKey.get(key);
      if (existing) existing.push(index);
      else { byKey.set(key, [index]); order.push(key); }
    });
    return order.map(function (key) {
      var indices = byKey.get(key);
      return {
        key: key,
        indices: indices,
        primaryIndex: indices[0],
        time: combineTime(indices.map(function (i) { return steps[i].time; }))
      };
    });
  }

  /* Keeps the FIRST step per dtcVariantId — a twice-daily product counts ONCE
   * in totals and cart quantities. Informational (no id) steps all kept. */
  function dedupeByVariant(steps) {
    var seen = new Set();
    var out = [];
    for (var i = 0; i < steps.length; i++) {
      var id = steps[i].dtcVariantId;
      if (!id) { out.push(steps[i]); continue; }
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(steps[i]);
    }
    return out;
  }

  /* ------------------------------------------------------------------ */
  /* category / time labels — ported from results/fwc/category.ts        */
  /* ------------------------------------------------------------------ */

  var CATEGORY_LABELS = [
    { prefix: 'HA', label: 'Hypoallergenic' },
    { prefix: 'C', label: 'Cleanser' },
    { prefix: 'E', label: 'Eye Cream' },
    { prefix: 'M', label: 'Moisturizer' },
    { prefix: 'S', label: 'Sunscreen' },
    { prefix: 'T', label: 'Treatment' }
  ];

  function categoryForSlot(slot) {
    if (!slot) return null;
    var normalized = String(slot).trim().toUpperCase();
    for (var i = 0; i < CATEGORY_LABELS.length; i++) {
      if (normalized.indexOf(CATEGORY_LABELS[i].prefix) === 0) return CATEGORY_LABELS[i].label;
    }
    return null;
  }

  function timeBadgeLabel(time) {
    if (time === 'AM') return 'AM';
    if (time === 'PM') return 'PM';
    if (time === 'BOTH') return 'AM + PM';
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* theming — canonical Baumann numbering + color math from prototype   */
  /* ------------------------------------------------------------------ */

  /* V2.3 hero accordion: the v2.1 one-paragraph description per type
     (prototype TYPEQUOTES, keyed by Baumann type number). */
  var TYPE_QUOTES = {
    1: 'Dry, sensitive skin with the potential for future wrinkles. A regimen that fortifies the skin barrier, reduces inflammation, and prepares skin to maintain collagen production with antioxidants and retinoids will help keep skin healthy and radiant over time.',
    2: 'Dry, sensitive skin that is prone to eventual signs of aging needs a regimen focused on strengthening the skin barrier, soothing inflammation, and maintaining hydration while preventing premature fine lines and wrinkles.',
    3: 'Sensitive, dry skin that is prone to hyperpigmentation and wrinkles requires a regimen that soothes irritation while brightening and supporting collagen production. Antioxidants, barrier-repairing ingredients, and gentle brighteners are essential.',
    4: 'Dry, sensitive skin with a tendency to wrinkle requires a skincare routine that reinforces the skin barrier, calms irritation, and delivers potent anti-aging ingredients like peptides and exosomes to support skin elasticity.',
    5: 'Sensitive, oily skin that is prone to future wrinkles benefits from a skincare routine that reduces inflammation, controls sebum, and supports long-term collagen production with gentle retinoids and antioxidants.',
    6: 'Oily, sensitive skin needs a balanced routine that controls oil, calms sensitivity, and prevents future aging. A combination of soothing ingredients and collagen-supporting active ingredients will help maintain youthful skin over time.',
    7: 'Oily, sensitive skin that is prone to hyperpigmentation and wrinkles needs a regimen that calms irritation, controls excess oil, and brightens skin tone with gentle yet effective ingredients such as antioxidants and soothing brighteners.',
    8: 'Oily, sensitive skin that wrinkles easily requires a regimen that soothes irritation while delivering anti-aging active ingredients like peptides and retinoids to maintain firmness and elasticity.',
    9: 'Managing oiliness, uneven skin tone, and future aging requires a regimen that balances sebum production, brightens the complexion, and prepares skin to maintain collagen production with antioxidants and retinoids.',
    10: 'Oily, resistant skin needs a routine that regulates oil, maintains hydration, and prevents future aging. A balanced regimen with antioxidants and retinoids will keep skin looking smooth and even over time.',
    11: 'Oily skin prone to hyperpigmentation and wrinkles requires a balanced approach to control oil, improve skin tone, and prevent aging. Antioxidants, retinoids, and pigment-correcting ingredients help maintain a healthy, even complexion.',
    12: 'Oily skin with a tendency to wrinkle benefits from a regimen that controls excess sebum while delivering potent anti-aging ingredients such as retinoids, peptides, and antioxidants to maintain a youthful appearance.',
    13: 'Dry skin with an uneven skin tone needs a regimen focused on hydration, skin barrier repair, and brightening ingredients. Antioxidants, retinoids, and collagen-boosting active ingredients help prevent visible signs of aging and maintain a youthful complexion.',
    14: 'Dry, resistant skin needs to maintain hydration and prevent future signs of aging. A well-balanced skincare routine with nourishing moisturizers, collagen-supporting ingredients, and sun protection will help preserve skin\u2019s youthful appearance.',
    15: 'Dry skin with uneven pigmentation and a susceptibility to wrinkles. Skincare regimens should include daily SPF, antioxidants, retinoids, moisturizers, alpha hydroxy acids, and ingredients to even skin tone.',
    16: 'Dry skin with a tendency to wrinkle requires a regimen focused on intense hydration, barrier repair, and anti-aging ingredients such as peptides, retinoids, and antioxidants to keep the skin smooth and resilient.'
  };

  var TYPE_NUM = {
    DSPT: 1, DSNT: 2, DSPW: 3, DSNW: 4, OSPT: 5, OSNT: 6, OSPW: 7, OSNW: 8,
    ORPT: 9, ORNT: 10, ORPW: 11, ORNW: 12, DRPT: 13, DRNT: 14, DRPW: 15, DRNW: 16
  };
  var AXES = {
    D: 'Dry', O: 'Oily', S: 'Sensitive', R: 'Resistant',
    P: 'Pigmented', N: 'Non-Pigmented', W: 'Wrinkle-Prone', T: 'Tight'
  };

  // Prototype-parity card descriptions: one short consumer sentence per
  // CATEGORY (derived from the slot letter — the regimen payload carries no
  // per-product marketing copy, and the raw INCI list is not shopper-facing).
  var CATEGORY_COPY = {
    C: 'Cleanses deeply. Removes oil and buildup from the skin, and prepares skin for other products by improving absorption.',
    T: 'Targets your skin’s specific concerns. A treatment step chosen for your Baumann Skin Type.',
    E: 'Cares for the delicate eye area. Helps reduce the look of puffiness and fine lines.',
    M: 'Moisturizes and supports the skin barrier. Chosen to balance your skin without feeling heavy.',
    S: 'Provides sun protection. Shields skin without clogging pores — lightweight enough for everyday wear.',
    H: 'A hypoallergenic option chosen for sensitive, easily-irritated skin.',
    K: 'Your complete dermatologist-curated routine in one kit — cleanser, treatment, moisturizer and SPF, matched to your skin type and sensitivity.'
  };

  function categoryCopy(step) {
    var letter = step && step.slot ? String(step.slot).charAt(0).toUpperCase() : '';
    if (letter === 'H' && String(step.slot).toUpperCase().indexOf('HA') === 0) letter = 'H';
    return CATEGORY_COPY[letter] || '';
  }
  var NEUTRAL = { hex: '#7B85B4', deep: '#5F6BA8', tint: '#E2E4F0' };

  /* Per-skin-type palette, lifted verbatim from the V2.3 prototype
     (p5-all-versions/export/STS Prototype V2.3). Types 1-16 walk a deliberate
     hue wheel — magenta → pink → coral → orange → amber → lime → green → teal
     → blue → indigo → violet — so the page visibly belongs to the visitor's
     type. The octagon grid already uses these, which is why an OSPW page with
     an indigo hero looked wrong next to its own amber octagon.

     These are the source of truth, ahead of shop.metafields.global.skinTypeInfo
     `color`, for two reasons:
       1. The metafield disagrees with the prototype (it has OSPW #f26689, a
          pink; the prototype and the octagon say #E2A857, amber).
       2. deep/tint here are hand-picked per type. Deriving them from one hex
          via deepen()/tintOf() does not reproduce the prototype's ramp.
     The metafield remains the fallback so a type absent here still themes. */
  var TYPE_PALETTE = {
    DSPT: { hex: '#A65E94', deep: '#85377F', tint: '#D3B0CF' }, //  1
    DSNT: { hex: '#D88AAE', deep: '#CC5D92', tint: '#EDCCDF' }, //  2
    DSPW: { hex: '#CB5D77', deep: '#C3365A', tint: '#E5B2BB' }, //  3
    DSNW: { hex: '#DEA796', deep: '#CD7D6B', tint: '#F2DAD1' }, //  4
    OSPT: { hex: '#D68762', deep: '#C95C3A', tint: '#EEC9AD' }, //  5
    OSNT: { hex: '#EEC8A7', deep: '#E2A77D', tint: '#F9EADB' }, //  6
    OSPW: { hex: '#E2A857', deep: '#D48132', tint: '#F5DAA9' }, //  7
    OSNW: { hex: '#F5DA95', deep: '#EDC369', tint: '#FCF1D0' }, //  8
    ORPT: { hex: '#ABC33F', deep: '#82B142', tint: '#DEE49B' }, //  9
    ORNT: { hex: '#C3D690', deep: '#A1C266', tint: '#EAEFCC' }, // 10
    ORPW: { hex: '#86B15A', deep: '#629F48', tint: '#CADBA9' }, // 11
    ORNW: { hex: '#A0C8AB', deep: '#75B187', tint: '#D9E8DB' }, // 12
    DRPT: { hex: '#6194B0', deep: '#35768D', tint: '#B3CBDD' }, // 13
    DRNT: { hex: '#9CB1D8', deep: '#718FC2', tint: '#D0DFF0' }, // 14
    DRPW: { hex: '#5970A3', deep: '#3B4D85', tint: '#ABB5D6' }, // 15
    DRNW: { hex: '#A498C1', deep: '#7D6DA3', tint: '#D7D2E6' }  // 16
  };

  function isHex(v) { return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v); }

  function hexToHsl(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255;
    var g = parseInt(hex.slice(3, 5), 16) / 255;
    var b = parseInt(hex.slice(5, 7), 16) / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    return { h: h, s: s, l: l };
  }

  function hslToHex(h, s, l) {
    var hue2rgb = function (p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p2 = 2 * l - q;
    var to = function (x) { return Math.round(hue2rgb(p2, q, x) * 255).toString(16).padStart(2, '0'); };
    return '#' + to(h + 1 / 3) + to(h) + to(h - 1 / 3);
  }

  /* Ported verbatim from the v2.3 prototype's brighten(): lighten to L=.72
   * unless the color is already light. */
  function brighten(hex) {
    var hsl = hexToHsl(hex);
    if (hsl.l >= 0.68) return hex;
    return hslToHex(hsl.h, hsl.s, 0.72);
  }

  function deepen(hex) {
    var hsl = hexToHsl(hex);
    return hslToHex(hsl.h, hsl.s, Math.max(0, hsl.l - 0.12));
  }

  /* --- text ink -----------------------------------------------------------
     TYPE_PALETTE is a DECORATION ramp (octagon fills, hero accents). Reusing
     `deep` for 10-12.5px labels is where it falls down: across the warm half of
     the hue wheel `deep` is not actually dark (OSNW deep is #EDC369, a pale
     gold), so small text set in it failed WCAG AA on every one of the 16 types
     when placed on a tint chip — 12 of them under even the 3.0 large-text floor.

     inkOf() walks lightness down until the colour clears `target` against the
     background it will actually sit on, so the result is still the type's own
     hue, just dark enough to read. Types whose `deep` already passes come back
     effectively unchanged, which keeps the visual identity intact and confines
     the change to the types that were genuinely illegible.

     Computed against the TINT because tint is darker than the white cards: any
     dark foreground that clears tint necessarily clears white, so one token
     safely covers both surfaces. */
  function relLum(hex) {
    var c = [1, 3, 5].map(function (i) {
      var v = parseInt(hex.slice(i, i + 2), 16) / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }

  function contrast(a, b) {
    var x = relLum(a), y = relLum(b);
    if (x < y) { var t = x; x = y; y = t; }
    return (x + 0.05) / (y + 0.05);
  }

  function inkOf(hex, bg, target) {
    if (!isHex(hex) || !isHex(bg)) return hex;
    var hsl = hexToHsl(hex);
    var ink = hex;
    // 2% steps down to near-black; bail the moment it clears the threshold.
    for (var l = hsl.l; l >= 0; l -= 0.02) {
      ink = hslToHex(hsl.h, hsl.s, Math.max(l, 0));
      if (contrast(ink, bg) >= target) return ink;
    }
    return ink;
  }

  function tintOf(hex) {
    var hsl = hexToHsl(hex);
    return hslToHex(hsl.h, hsl.s, Math.max(hsl.l, 0.88));
  }

  /* SHOP-7: the amounts are presentment values from Shopify, so the SYMBOL has
   * to follow the shopper's market too — a corrected number rendered as "$202"
   * when the charge is £202 is still a lie. USD output is byte-identical to the
   * previous hardcoded behaviour (incl. dropping a trailing .00), so nothing
   * changes for the US store; other markets get their real symbol. */
  function currencyCode() {
    // Prefer the code Shopify returned alongside the price itself — that is the
    // currency the amount is actually denominated in. Fall back to the section
    // attribute, then to the global, then USD.
    if (S && typeof S.currency === 'string' && S.currency.length === 3) return S.currency;
    try {
      var c = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      return (typeof c === 'string' && c.length === 3) ? c : 'USD';
    } catch (e) { return 'USD'; }
  }
  function money(n) {
    var code = currencyCode();
    if (code === 'USD') return '$' + n.toFixed(2).replace(/\.00$/, '');
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2).replace(/\.00$/, '');
    }
  }
  function priceText(p) { return (p === null || p === undefined) ? '—' : money(p); }
  function moneyCents(c) { return money(c / 100); }

  /* ------------------------------------------------------------------ */
  /* engine state                                                        */
  /* ------------------------------------------------------------------ */

  var S = null; // single active instance

  function qsEl(root, name) { return root.querySelector('[data-slq-el="' + name + '"]'); }

  function cacheDom(root) {
    var els = {};
    var nodes = root.querySelectorAll('[data-slq-el]');
    for (var i = 0; i < nodes.length; i++) {
      var name = nodes[i].getAttribute('data-slq-el');
      if (!(name in els)) els[name] = nodes[i]; // first (static) wins; template content is inert and never matched here
    }
    var tpls = {};
    var tplNodes = root.querySelectorAll('template[data-slq-tpl]');
    for (var j = 0; j < tplNodes.length; j++) {
      tpls[tplNodes[j].getAttribute('data-slq-tpl')] = tplNodes[j];
    }
    return { els: els, tpls: tpls };
  }

  function clone(name) {
    var tpl = S.tpls[name];
    if (!tpl) return null;
    return tpl.content.firstElementChild.cloneNode(true);
  }

  function tel(node, name) { return node.querySelector('[data-slq-el="' + name + '"]'); }

  /* ------------------------------------------------------------------ */
  /* derived state                                                       */
  /* ------------------------------------------------------------------ */

  function effectiveSteps() { return applySwaps(S.steps, S.swaps); }

  function tierFor(size) { return size === 'core' ? 'starter' : 'complete'; }

  /* ------------------------------------------------------------------ */
  /* Essentials Kit (third segment) — the store's real per-type kit SKU  */
  /* ------------------------------------------------------------------ */

  // R-types have one kit; S-types split by sensitivity (Luis: kits are
  // "already sorted by skin type and sensitivity").
  var SENSITIVITY_KIT = { S1: 'acne', S2: 'redness', S3: 'stinging', S4: 'allergic' };

  function kitHandle() {
    var t = (S.skinType || '').toLowerCase();
    if (!/^[od][sr][pn][wt]$/.test(t)) return null;
    if (t.charAt(1) === 's') {
      var seg = SENSITIVITY_KIT[(S.sensitivity || '').toUpperCase()] || 'acne';
      return t + '-' + seg + '-essentials-bundle';
    }
    return t + '-essentials-bundle';
  }

  /* Bundles are switched OFF for now (Luis, 2026-08-17): the Essentials Kit
     tab is never offered and the kit product is never fetched. Flip this to
     true to bring the Bundle segment back — everything else is still wired. */
  var KITS_ENABLED = false;
  function ensureKit() {
    if (!KITS_ENABLED) { if (S.kit) S.kit.status = 'error'; return; }
    if (!S.kit || S.kit.status === 'loading' || S.kit.status === 'done' || S.kit.status === 'error') return;
    var handle = kitHandle();
    if (!handle) { S.kit.status = 'error'; return; }
    S.kit.status = 'loading';
    fetch('/products/' + handle + '.js')
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (p) {
        // base variant = the SKU without a sunscreen suffix; else first available
        var variants = p.variants || [];
        var base = null;
        variants.forEach(function (v) {
          if (/^[A-Z]+(-S\d)?-ESS-BUNDLE$/.test(v.sku || '') && (base === null || v.available)) {
            if (!base || (v.available && !base.available)) base = v;
          }
        });
        if (!base) base = variants.filter(function (v) { return v.available; })[0] || variants[0];
        if (!base) throw new Error('kit has no variants');
        S.kit.product = p;
        S.kit.step = {
          slot: 'KIT', tier: 'starter', time: null, swapCandidates: [],
          title: p.title, brand: null,
          image: (p.featured_image || (p.images && p.images[0]) || null),
          priceMsrp: base.price / 100, dtcVariantId: String(base.id),
          inStock: base.available !== false, handle: handle,
          keyIngredients: [], ingredientsSummary: null, url: '/products/' + handle,
          substituted: false, kit: true
        };
        S.kit.status = 'done';
        settleKitSegment();
        scheduleRender();
      })
      .catch(function () { S.kit.status = 'error'; settleKitSegment(); scheduleRender(); });
  }

  /* The Essentials Kit is only offered when it can actually be bought. 17 of
     the 40 type/sensitivity kits currently sit at zero inventory on every
     variant, and a tab that can only say "out of stock" is a dead end. Still
     loading counts as unavailable, so the tab appears once confirmed rather
     than disappearing from under the cursor. */
  function kitAvailable() {
    if (!KITS_ENABLED) return false;
    return !!(S.kit && S.kit.status === 'done' && S.kit.step && isBundleAddableStep(S.kit.step));
  }

  // Defensive: the tab is hidden while unavailable, so this should be
  // unreachable — but never strand the visitor on a segment they can't buy.
  function settleKitSegment() {
    if (S.size === 'kit' && !kitAvailable()) S.size = 'core';
  }

  function buildView() {
    if (S.size === 'kit') {
      if (!S.kit || S.kit.status !== 'done') {
        ensureKit();
        return { eff: [], groups: [], kitMode: true, kitPending: !S.kit || S.kit.status !== 'error', kitError: S.kit && S.kit.status === 'error' };
      }
      var ks = S.kit.step;
      return {
        eff: [ks], kitMode: true,
        groups: [{ key: 'kit', indices: [], primaryIndex: 0, time: null, step: ks, orig: ks, inTier: true, removed: false, kit: true }]
      };
    }
    var eff = effectiveSteps();
    var groups = buildStepGroups(eff);
    var tierIdx = new Set(filterStepIndicesByTier(eff, tierFor(S.size)));
    var view = groups.map(function (g) {
      return {
        key: g.key,
        indices: g.indices,
        primaryIndex: g.primaryIndex,
        time: g.time,
        step: eff[g.primaryIndex],
        orig: S.steps[g.primaryIndex],
        inTier: tierIdx.has(g.primaryIndex),
        removed: g.indices.every(function (i) { return S.removed.has(i); })
      };
    });
    return { eff: eff, groups: view };
  }

  function groupPassesTime(g, filter) {
    if (g.kit) return true; // the kit is the whole routine — no AM/PM split
    if (filter === 'all') return true;
    if (filter === 'am') return g.time === 'AM' || g.time === 'BOTH';
    return g.time === 'PM' || g.time === 'BOTH';
  }

  /* Defence in depth for the 2026-08-10 incident: a hypoallergenic regimen is
     built entirely from HA slots, and the server tiered every one of them
     `complete`, so the starter view ("3-4 Step Regimen") the page opens on
     matched NOTHING — 0 products, $0, on a page still headed QUIZ COMPLETE.
     Fixed server-side in widget/regimen.ts, but the page must not be able to
     show an empty routine even if a payload like that reaches it again: if the
     tier filter removes everything the visitor has not removed themselves,
     ignore the tier rather than render nothing. */
  function activeGroups(view) {
    var inTier = view.groups.filter(function (g) { return g.inTier && !g.removed; });
    if (inTier.length) return inTier;
    return view.groups.filter(function (g) { return !g.removed; });
  }

  function removedGroups(view) {
    return view.groups.filter(function (g) { return g.inTier && g.removed; });
  }

  /* Deduped purchase UNITS (a twice-daily product is one physical bottle). */
  function unitSteps(view) {
    return dedupeByVariant(activeGroups(view).map(function (g) { return g.step; }));
  }

  function subFor(step) {
    return step.dtcVariantId ? (S.subs.get(String(step.dtcVariantId)) || null) : null;
  }

  /* {msrp, now, savings} in dollars over bundle-addable deduped units;
   * subscribed units price at their REAL selling-plan allocation. */
  function computeTotals(view) {
    var msrp = 0, now = 0;
    unitSteps(view).forEach(function (u) {
      if (!isBundleAddableStep(u)) return;
      msrp += u.priceMsrp;
      var sub = subFor(u);
      now += (sub && sub.priceCents !== null && sub.priceCents !== undefined) ? sub.priceCents / 100 : u.priceMsrp;
    });
    return { msrp: msrp, now: now, savings: msrp - now };
  }

  /* ------------------------------------------------------------------ */
  /* presentment pricing (SHOP-7)                                        */
  /* ------------------------------------------------------------------ */
  /* The regimen API serves `priceMsrp` from cached_products, which is always
   * USD. This store sells through Shopify Markets, where each market has its
   * OWN price list — not an FX conversion of the US price. Measured on the live
   * store, one product: US $19.00 / GB 16.00 / CA 30.00 / AU 30.00 / DE 18.95.
   * Across a real routine that made the quiz misquote by CA +54%, AU +57%,
   * GB −17% — i.e. we promised a total and checkout charged something else.
   *
   * `/products/{handle}.js` is fetched already (for selling plans) and its
   * response IS market-aware, so the correct number is in hand; it was simply
   * discarded. Reconciling at the source means all 23 downstream money call
   * sites become correct without touching any of them.
   *
   * `compare_at_price` is reconciled in the SAME step, never separately: a
   * market price next to a USD compare-at would manufacture a fake discount
   * and a bogus "% OFF" badge. */
  var presentmentByVariant = Object.create(null);

  /* Ask Shopify to price EVERY variant on the page — routine steps, swap
   * candidates and the kit — in the buyer's market, in a single Storefront API
   * call. This is the authoritative source; the DB figure is only a fallback
   * for when the call fails.
   *
   * One batched request rather than per-product `/products/{handle}.js`: a
   * results render already makes ~8 AJAX calls and Shopify starts returning
   * 429s, so fanning out per candidate was not viable. It also returns the
   * currencyCode explicitly instead of us inferring it.
   *
   * The country comes from Liquid (`localization.country.iso_code`) and is
   * shape-checked before use — `country` is a CountryCode ENUM, so it rides as
   * a typed variable and never as string interpolation. */
  function fetchPresentmentPrices() {
    if (!S || !S.sfDomain || !S.sfToken) return Promise.resolve(0);
    var country = /^[A-Z]{2}$/.test(String(S.country || '')) ? S.country : 'US';

    var ids = [];
    var push = function (o) {
      if (!o || o.dtcVariantId === null || o.dtcVariantId === undefined) return;
      var gid = 'gid://shopify/ProductVariant/' + String(o.dtcVariantId);
      if (ids.indexOf(gid) === -1) ids.push(gid);
    };
    (S.steps || []).forEach(function (st) {
      push(st);
      (st.swapCandidates || []).forEach(push);
    });
    if (S.kit && S.kit.step) push(S.kit.step);
    if (!ids.length) return Promise.resolve(0);

    var query = 'query SlqPrices($ids:[ID!]!,$country:CountryCode!) @inContext(country:$country){'
      + 'nodes(ids:$ids){... on ProductVariant{id price{amount currencyCode} compareAtPrice{amount}}}}';

    return fetch('https://' + S.sfDomain + '/api/2024-10/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': S.sfToken },
      body: JSON.stringify({ query: query, variables: { ids: ids, country: country } }),
    })
      .then(function (r) { if (!r.ok) throw new Error('storefront ' + r.status); return r.json(); })
      .then(function (j) {
        if (!j || !j.data || !Array.isArray(j.data.nodes)) throw new Error('storefront payload');
        j.data.nodes.forEach(function (n) {
          if (!n || !n.id || !n.price) return;
          var vid = String(n.id).split('/').pop();
          var price = Math.round(parseFloat(n.price.amount) * 100);
          if (!isFinite(price)) return;
          var cmp = n.compareAtPrice ? Math.round(parseFloat(n.compareAtPrice.amount) * 100) : null;
          presentmentByVariant[vid] = {
            price: price,
            compareAt: (cmp !== null && isFinite(cmp) && cmp > price) ? cmp : null,
          };
          if (n.price.currencyCode) S.currency = n.price.currencyCode;
        });
        var changed = applyPresentmentPrices();
        if (changed) scheduleRender();
        return changed;
      })
      .catch(function () {
        // Honest fallback: keep the DB figures. The per-product reconciliation
        // below still corrects the displayed steps.
        return 0;
      });
  }

  function recordPresentmentPrice(v) {
    if (!v || v.id === null || v.id === undefined) return;
    if (typeof v.price !== 'number') return;
    presentmentByVariant[String(v.id)] = {
      price: v.price,
      compareAt: (typeof v.compare_at_price === 'number' && v.compare_at_price > v.price)
        ? v.compare_at_price : null,
    };
  }

  function applyPresentmentTo(obj) {
    if (!obj || obj.dtcVariantId === null || obj.dtcVariantId === undefined) return false;
    var p = presentmentByVariant[String(obj.dtcVariantId)];
    if (!p) return false;
    var price = p.price / 100;
    var compareAt = p.compareAt === null ? null : p.compareAt / 100;
    if (obj.priceMsrp === price && (obj.compareAt || null) === compareAt) return false;
    obj.priceMsrp = price;
    obj.compareAt = compareAt;
    return true;
  }

  /* Idempotent: safe to call after every product fetch resolves. */
  function applyPresentmentPrices() {
    if (!S || !S.steps) return 0;
    var changed = 0;
    S.steps.forEach(function (step) {
      if (applyPresentmentTo(step)) changed++;
      (step.swapCandidates || []).forEach(function (c) { if (applyPresentmentTo(c)) changed++; });
    });
    Object.keys(S.swaps || {}).forEach(function (k) { if (applyPresentmentTo(S.swaps[k])) changed++; });
    if (S.kit && S.kit.step && applyPresentmentTo(S.kit.step)) changed++;
    return changed;
  }

  /* ------------------------------------------------------------------ */
  /* selling plans (Subscribe & Save) — /products/{handle}.js, never-guess */
  /* ------------------------------------------------------------------ */

  /* Normalise a selling-plan cadence to DAYS.
   *
   * WHY THIS EXISTS: cadence used to be matched by comparing plan NAME strings.
   * Vendors name the same interval differently — "Delivery every 30 days" vs
   * "Delivery every 1 month" — so a shopper who picked "every 1 month" got that
   * cadence only on products whose vendor happened to use that exact wording;
   * every other product silently kept a different plan (observed: choosing
   * "every 3 months" still billed two products every 30 days). Comparing
   * intervals instead of prose is the only way that can't drift.
   *
   * Returns null when nothing parses, so callers can skip rather than guess. */
  function cadenceDays(name, optionValue) {
    var src = String(optionValue || '') + ' ' + String(name || '');
    var m = src.match(/(\d+)\s*(day|days|week|weeks|month|months|year|years)/i);
    if (!m) return null;
    var n = parseInt(m[1], 10);
    if (!isFinite(n) || n <= 0) return null;
    var unit = m[2].toLowerCase();
    if (unit.indexOf('day') === 0) return n;
    if (unit.indexOf('week') === 0) return n * 7;
    if (unit.indexOf('month') === 0) return n * 30;
    return n * 365;
  }

  /* Two cadences are "the same" if they land within a few days of each other —
   * 30 days and 1 month are the same delivery rhythm to a shopper. */
  function sameCadence(a, b) {
    if (a === null || b === null) return false;
    return Math.abs(a - b) <= 3;
  }

  /* Canonical shopper-facing label for an interval, so the chip row never shows
   * "every 30 days" and "every 1 month" as if they were different choices.
   *
   * DAYS is the canonical unit, deliberately. It is what ~5,300 of the ~5,340
   * subscription plans in the catalog are actually named ("Delivery every
   * 30/45/60 days"), and — the part that matters — it is the string Shopify
   * prints on each checkout line, because checkout renders the selling plan's
   * own name and we cannot override it. Rendering months here made our own
   * chip say "Delivery every 1 month" while the very next screen said
   * "Delivery every 30 days" for the same plan. Anything that is a whole
   * number of days is therefore labelled in days, so the two always agree. */
  function cadenceLabel(days) {
    if (days === null) return '';
    return 'Delivery every ' + days + (days === 1 ? ' day' : ' days');
  }

  /* Max (v6, 2026-08-20): the FRONT-FACING cadence reads "Monthly" /
   * "Bimonthly", as small as possible, on both Subscribe & Save surfaces.
   * Only the two marketed rhythms get a word; any other interval (the 45-day
   * plans) keeps its honest day label so we never imply a rhythm a plan does
   * not have. The ranges absorb the vendors' vocabulary for the same rhythm:
   * 30 days / 1 month / 4 weeks land on Monthly, 60 days / 2 months / 8 weeks
   * on Bimonthly. cadenceLabel stays as the plan-name matcher (planNearest
   * tie-breaks) and the fallback, because checkout prints the selling plan's
   * OWN name ("Delivery every 30 days") and that cannot be overridden. */
  function cadenceMarketingName(days) {
    if (days === null || days === undefined) return null;
    if (days >= 28 && days <= 31) return 'Monthly';
    if (days >= 56 && days <= 62) return 'Bimonthly';
    return null;
  }

  function cadenceShopperLabel(days, compact) {
    var word = cadenceMarketingName(days);
    if (word) return word;
    var label = cadenceLabel(days);
    // Sticky bar on a phone: "Delivery every 45 days" does not fit the row;
    // "Every 45 days" does (the bar already says Subscribe & Save).
    return compact ? label.replace(/^Delivery every /i, 'Every ') : label;
  }

  function ensurePlans(step) {
    if (!step || !step.handle || !step.dtcVariantId) return;
    var handle = String(step.handle).replace(/^\/+/, '');
    if (S.planCache.has(handle)) return;
    S.planCache.set(handle, { status: 'loading', byVariant: {} });
    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { if (!r.ok) throw new Error('product json ' + r.status); return r.json(); })
      .then(function (product) {
        var nameById = {};
        var optionsById = {};
        var groups = product.selling_plan_groups || [];
        groups.forEach(function (g) {
          (g.selling_plans || []).forEach(function (p) {
            if (p && p.id !== null && p.id !== undefined && p.name) {
              nameById[String(p.id)] = String(p.name);
              // `options` carries the vendor's own cadence value ("30 Days",
              // "1 Month"); it parses more reliably than the display name.
              optionsById[String(p.id)] = (p.options || [])
                .map(function (o) { return o && o.value; })
                .filter(Boolean)
                .join(' ');
            }
          });
        });
        var byVariant = {};
        (product.variants || []).forEach(function (v) {
          var opts = [];
          (v.selling_plan_allocations || []).forEach(function (a) {
            var planId = a.selling_plan_id;
            var name = nameById[String(planId)];
            if (!name) return; // allocation without a named plan — dropped, never guessed
            var priceCents = (a.per_delivery_price !== null && a.per_delivery_price !== undefined)
              ? a.per_delivery_price
              : (a.price !== null && a.price !== undefined ? a.price : null);
            opts.push({
              id: Number(planId),
              name: name,
              priceCents: priceCents,
              // Vendors name the SAME cadence differently ("every 30 days" vs
              // "every 1 month"), so the cadence chips must compare intervals,
              // never name strings — see cadenceDays.
              intervalDays: cadenceDays(name, optionsById[String(planId)])
            });
          });
          byVariant[String(v.id)] = opts;
          // SHOP-7: this response is already market-aware, so `v.price` is what
          // the shopper will ACTUALLY be charged. We were throwing it away and
          // rendering the USD figure from cached_products instead.
          recordPresentmentPrice(v);
        });
        S.planCache.set(handle, { status: 'done', byVariant: byVariant });
        applyPresentmentPrices();
        // Plans for this product just landed; this may have been the last one
        // the default pass was waiting on.
        maybeDefaultSubscribeOn();
        scheduleRender();
      })
      .catch(function () {
        // honest failure: no plans shown for this product (toggle stays hidden)
        S.planCache.set(handle, { status: 'error', byVariant: {} });
      });
  }

  /* [] = definitively none; null = unknown yet (loading/failed fetch pending) */
  function plansForStep(step) {
    if (!step || !step.handle || !step.dtcVariantId) return [];
    var entry = S.planCache.get(String(step.handle).replace(/^\/+/, ''));
    if (!entry) return null;
    if (entry.status === 'loading') return null;
    return entry.byVariant[String(step.dtcVariantId)] || [];
  }

  function setSubscription(step, on, planId) {
    if (!step.dtcVariantId) return;
    var key = String(step.dtcVariantId);
    if (!on) { S.subs.delete(key); scheduleRender(); return; }
    var opts = plansForStep(step) || [];
    if (!opts.length) return; // never-guess: no real plan, no subscription
    var chosen = null;
    for (var i = 0; i < opts.length; i++) if (String(opts[i].id) === String(planId)) chosen = opts[i];
    if (!chosen) chosen = opts[0];
    S.subs.set(key, {
      planId: chosen.id,
      planName: chosen.name,
      priceCents: chosen.priceCents,
      intervalDays: chosen.intervalDays
    });
    scheduleRender();
  }

  /* The cadences EVERY subscribable product can honour, shortest first.
   * Single source of truth for both the cadence chips and the default picked
   * when Subscribe & Save is switched on — they used to be derived separately,
   * which is exactly how the shopper ended up with a cart mixing 30-day and
   * 60-day plans. */
  function commonCadences(bearing) {
    if (!bearing || !bearing.length) return [];
    var out = [];
    (plansForStep(bearing[0]) || []).forEach(function (o) {
      if (o.intervalDays === null || o.intervalDays === undefined) return;
      if (out.some(function (d) { return sameCadence(d, o.intervalDays); })) return;
      var everyoneHasIt = bearing.every(function (u) {
        return (plansForStep(u) || []).some(function (p) { return sameCadence(p.intervalDays, o.intervalDays); });
      });
      if (everyoneHasIt) out.push(o.intervalDays);
    });
    out.sort(function (a, b) { return a - b; });
    return out;
  }

  /* Nearest plan to a target interval; null when nothing is parseable.
   *
   * Ties are broken toward the plan whose vendor name already matches the
   * canonical label. A product can carry two plans for the SAME rhythm under
   * different vocabulary — Zerafite Wrinkle Defense is enrolled in both
   * "Delivery every 1 month" (legacy, kept alive for 33 existing subscribers)
   * and "Delivery every 30 days" (the catalog standard). Both parse to 30, so
   * without a tie-break the winner was whichever Shopify happened to list
   * first, and the checkout line could read "every 1 month" while every other
   * product in the same cart read "every 30 days". */
  function planNearest(opts, days) {
    var best = null, bestDelta = Infinity, bestCanonical = false;
    (opts || []).forEach(function (o) {
      if (o.intervalDays === null || o.intervalDays === undefined) return;
      var delta = Math.abs(o.intervalDays - days);
      var canonical = String(o.name || '').toLowerCase() === cadenceLabel(o.intervalDays).toLowerCase();
      if (delta < bestDelta || (delta === bestDelta && canonical && !bestCanonical)) {
        bestDelta = delta; best = o; bestCanonical = canonical;
      }
    });
    return best;
  }

  function planBearingUnits(view) {
    return unitSteps(view).filter(function (u) {
      if (!isBundleAddableStep(u)) return false;
      var opts = plansForStep(u);
      return !!(opts && opts.length);
    });
  }

  /* Subscribe & Save is a 15%-off-every-refill offer. A handful of products
     carry a thinner subscription plan (Plated Skin Science: 5%, intended by
     the brand) — those are NOT switched on by default and do not count towards
     the master toggle or the "Subscribe & Save 15%" figure; the shopper can
     still opt one in from its own card (Luis, 2026-08-18). Threshold with a
     little slack so 14.9% rounding never demotes a real 15% plan. */
  var DEFAULT_SUB_MIN_SAVING = 0.145;

  function bestPlanFor(u) {
    var best = null;
    (plansForStep(u) || []).forEach(function (o) {
      if (o.priceCents !== null && o.priceCents !== undefined && (!best || o.priceCents < best.priceCents)) best = o;
    });
    return best;
  }

  function isThinPlanUnit(u) {
    var best = bestPlanFor(u);
    if (!best || u.priceMsrp === null || u.priceMsrp === undefined || !(u.priceMsrp > 0)) return false;
    return (u.priceMsrp - best.priceCents / 100) / u.priceMsrp < DEFAULT_SUB_MIN_SAVING;
  }

  /* Plan-bearing units the default and the master toggle act on. A thin-plan
     product is NEVER in this set (Luis, 2026-08-18: "not ever as default") —
     a routine made only of thin-plan products gets no master Subscribe & Save
     offer at all; each product's own card still carries its toggle. */
  function defaultSubUnits(view) {
    return planBearingUnits(view).filter(function (u) { return !isThinPlanUnit(u); });
  }

  function masterSubState(view) {
    var eligible = defaultSubUnits(view);
    if (!eligible.length) return { available: false, on: false };
    // Promo gate: Subscribe & Save is offered at 3+ products, or on the
    // Essentials Kit (which bundles the routine into one SKU).
    if (!view.kitMode && unitSteps(view).length < 3) return { available: false, on: false };
    var allOn = eligible.every(function (u) { return !!subFor(u); });
    return { available: true, on: allOn };
  }

  function toggleMasterSub() {
    var view = buildView();
    var st = masterSubState(view);
    if (!st.available) return;
    var bearing = planBearingUnits(view);
    if (st.on) {
      /* Switching OFF clears EVERY subscription, not just the ones on screen.
         Clearing only the current view left products that are subscribed but
         not currently visible — opt out from "3-4 Step" and the four extra
         "5+ Step" products stayed on recurring plans, so the visitor was told
         "one-time purchase" and still had four subscriptions waiting in the
         larger view. "Switched to a one-time purchase" has to mean all of it. */
      S.subOptedOut = true;
      S.subs.clear();
      toast('Switched to a one-time purchase.');
    } else {
      /* Turning it ON means "subscribe whatever can be subscribed", including
         products not yet on screen — so forget which units the default pass has
         already offered, letting units revealed later (5+ Step, a swap) pick up
         the subscription too and keep the toggle honest. */
      S.subOptedOut = false;
      S.subConsidered.clear();
      subscribeAll(defaultSubUnits(view));
      toast('Subscribe & Save turned on for every product with 15% off refills.');
    }
    bearing.forEach(function (u) { S.subConsidered.add(String(u.dtcVariantId)); });
    scheduleRender();
  }

  /* Shared by the visitor's toggle and the on-by-default pass, so the two can
     never drift. Default to the shortest cadence every product can actually
     honour, so the whole routine arrives on ONE rhythm. Picking each product's
     opts[0] independently is what produced carts mixing 30- and 60-day
     deliveries. */
  function subscribeAll(bearing) {
    var common = commonCadences(bearing);
    var target = common.length ? common[0] : null;
    bearing.forEach(function (u) {
      if (subFor(u)) return; // keep the visitor's own cadence choice
      var opts = plansForStep(u) || [];
      if (!opts.length) return;
      var chosen = (target === null) ? opts[0] : (planNearest(opts, target) || opts[0]);
      S.subs.set(String(u.dtcVariantId), {
        planId: chosen.id,
        planName: chosen.name,
        priceCents: chosen.priceCents,
        intervalDays: chosen.intervalDays
      });
    });
  }

  /* Subscribe & Save starts ON. It is the better offer for the shopper (15% off
     every refill plus the $10 first-order code) and the routine is designed to
     be repurchased, so opting OUT should be the deliberate act rather than
     opting in.

     Runs only once the plan data every bearing unit needs has actually arrived
     — plansForStep() returns null while a product's plans are still loading,
     and turning "everything" on mid-load would subscribe only the products
     that happened to resolve first.

     It applies PER UNIT rather than once per page. A once-per-page latch meant
     the pass ran on the "3-4 Step" view and never again, so the extra products
     revealed by "5+ Step" were left unsubscribed; masterSubState then reported
     "not all on" and drew the toggle OFF while the five already-subscribed
     products still carried recurring plans into checkout. The visitor was shown
     OFF and would have been billed monthly for five items.

     Two guards keep it honest in the other direction: nothing auto-subscribes
     once the visitor has opted out, and a unit is only ever offered the default
     ONCE, so unsubscribing a single product by hand is never undone. */
  function maybeDefaultSubscribeOn() {
    if (!S || S.subOptedOut) return;
    var view = buildView();
    var st = masterSubState(view);
    if (!st.available) return;
    /* Wait on EVERY addable unit, not on planBearingUnits(): that helper
       already drops units whose plans are still loading (plansForStep returns
       null), so asking IT about loading can never be true and the default
       fired on whatever had resolved first — measured as one of four products
       subscribed, "$149.99 -> $143.69, save $6.30" instead of the full 15%. */
    var addable = unitSteps(view).filter(isBundleAddableStep);
    if (addable.some(function (u) { return plansForStep(u) === null; })) return;
    var bearing = planBearingUnits(view);
    if (!bearing.length) return;
    var eligible = defaultSubUnits(view);
    var fresh = eligible.filter(function (u) {
      return !S.subConsidered.has(String(u.dtcVariantId));
    });
    if (!fresh.length) return;
    bearing.forEach(function (u) { S.subConsidered.add(String(u.dtcVariantId)); });
    subscribeAll(fresh);
    scheduleRender();
  }

  /* ------------------------------------------------------------------ */
  /* interactions                                                        */
  /* ------------------------------------------------------------------ */

  function labelForGroup(g) {
    return categoryForSlot(g.step.slot) || g.step.title || 'This step';
  }

  /* The remove control needs the PRODUCT, not the category: a routine routinely
   * holds two cleansers, and "Remove Cleanser" twice tells a screen-reader user
   * nothing about which one they are about to drop. */
  function removeLabelForGroup(g) {
    return 'Remove ' + (g.step.title || labelForGroup(g));
  }

  function doSwapGroup(g, candidate) {
    var origVariant = g.orig && g.orig.dtcVariantId;
    var isRevert = origVariant && candidate.dtcVariantId && String(candidate.dtcVariantId) === String(origVariant);
    g.indices.forEach(function (i) {
      if (isRevert) delete S.swaps[i];
      else S.swaps[i] = candidate;
    });
    closeDrawer();
    flash(candidate, g);
    var name = candidate.title || 'this product';
    toast(g.time === 'BOTH'
      ? 'Swapped to ' + name + ' in your morning and evening steps'
      : 'Swapped to ' + name + '. Routine total updated.');
    scheduleRender();
  }

  function flash(candidate, g) {
    // the post-swap group key: applySwap keeps the slot (so the category), and
    // a valid candidate always carries a variant id - same rule as grouping
    S.flashKey = groupKeyFor(Object.assign({}, g.step, { dtcVariantId: candidate.dtcVariantId }), g.primaryIndex);
    clearTimeout(S.flashTimer);
    S.flashTimer = setTimeout(function () { S.flashKey = null; scheduleRender(); }, 2000);
  }

  function removeGroup(g) {
    if (g.kit) return; // the kit can't be removed from its own view
    g.indices.forEach(function (i) { S.removed.add(i); });
    if (S.mobOpen === g.key) S.mobOpen = null;
    toast(labelForGroup(g) + ' removed. Total updated. Add it back any time below your Daily Protocol.');
    scheduleRender();
  }

  function addBackGroup(g) {
    g.indices.forEach(function (i) { S.removed.delete(i); });
    toast(labelForGroup(g) + ' added back to your routine.');
    scheduleRender();
  }

  function setSize(size) {
    if (S.size === size) return;
    if (size === 'kit' && !kitAvailable()) return; // tab is hidden; ignore stray calls
    S.size = size;
    if (size === 'kit') { S.tod = 'all'; S.glanceTab = 'all'; }
    // A bigger regimen reveals products the default pass has never seen. Offer
    // them the subscription too, so the toggle keeps telling the truth about
    // what is actually in the cart (plans that arrive later re-trigger this
    // from the fetch handler).
    maybeDefaultSubscribeOn();
    toast(size === 'kit' ? 'Showing your Bundle — everything in one box'
      : size === 'plus' ? 'Expanded to your full regimen' : 'Back to the core regimen');
    scheduleRender();
  }

  function setTod(k) {
    S.tod = k;
    S.glanceTab = k;
    S.mobOpen = null;
    scheduleRender();
  }

  /* ------------------------------------------------------------------ */
  /* swap drawer                                                         */
  /* ------------------------------------------------------------------ */

  /* The drawer is a modal in every respect except its semantics: it covers the
     page behind a scrim and traps the user's attention, but it shipped with no
     role, no accessible name, and no focus handling at all. A keyboard user
     opened it and kept tabbing through the 19 controls inside it AND straight
     on into the page underneath, with Escape doing nothing. These three helpers
     give it the behaviour its appearance already promises. */
  function drawerEl() { return S.root.querySelector('[data-slq-drawer]'); }

  function focusablesIn(el) {
    return [...el.querySelectorAll('button,a[href],input,select,textarea,[tabindex]')]
      .filter(function (n) {
        return n.tabIndex >= 0 && !n.disabled && n.getClientRects().length > 0;
      });
  }

  function onDrawerKeydown(e) {
    var d = drawerEl();
    if (!d || d.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    // Focus trap: keep Tab inside the drawer while it is open.
    var f = focusablesIn(d);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || !d.contains(document.activeElement))) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function openDrawer(groupKey, from) {
    S.drawerKey = groupKey;
    S.drawerFrom = from;
    // Remember what opened it so focus can go back there on close — otherwise
    // focus lands on <body> and the user restarts from the top of the page.
    // Store the KEY, not the element: closing re-renders the protocol cards, so
    // the button that opened the drawer is destroyed and a fresh one takes its
    // place. Holding the node would leave us focusing a detached element.
    S.drawerReturnKey = groupKey;
    S.drawerReturnFrom = from;
    document.addEventListener('keydown', onDrawerKeydown, true);
    S.pendingFocus = { kind: 'drawer' };
    scheduleRender();
  }

  function closeDrawer() {
    S.drawerKey = null;
    document.removeEventListener('keydown', onDrawerKeydown, true);
    var key = S.drawerReturnKey;
    S.drawerReturnKey = null;
    S.drawerReturnFrom = null;
    if (key) S.pendingFocus = { kind: 'swap-trigger', key: key };
    scheduleRender();
  }

  function candidateList(g, view) {
    var current = g.step;
    var list = [];
    var seen = new Set();
    var push = function (entry) {
      var id = entry.candidate.dtcVariantId;
      var key = id ? String(id) : 'x:' + list.length;
      if (seen.has(key)) return;
      seen.add(key);
      list.push(entry);
    };
    if (current.dtcVariantId && current.title) {
      push({ candidate: current, isCurrent: true, isOriginal: false });
    }
    var orig = g.orig;
    if (orig && isValidSwapCandidate(orig) && (!current.dtcVariantId || String(orig.dtcVariantId) !== String(current.dtcVariantId))) {
      push({ candidate: orig, isCurrent: false, isOriginal: true });
    }
    (current.swapCandidates || []).forEach(function (c) {
      if (!isValidSwapCandidate(c)) return;
      if (current.dtcVariantId && String(c.dtcVariantId) === String(current.dtcVariantId)) return;
      push({ candidate: c, isCurrent: false, isOriginal: false });
    });
    // Variants already used by ANOTHER active step of the routine. These used
    // to be greyed out ("Used elsewhere") — but the same cleanser morning and
    // evening is a perfectly good routine and the cheaper one: totals and the
    // cart already count a twice-daily product ONCE (dedupeByVariant /
    // buildCartItems), so picking it here drops this step's own bottle. Flag
    // it, keep it selectable, and say what it saves (Luis, 2026-08-18).
    var taken = new Set();
    activeGroups(view).forEach(function (other) {
      if (other.key === g.key) return;
      if (other.step.dtcVariantId) taken.add(String(other.step.dtcVariantId));
    });
    var currentShared = !!(current.dtcVariantId && taken.has(String(current.dtcVariantId)));
    list.forEach(function (entry) {
      entry.taken = !entry.isCurrent && !!entry.candidate.dtcVariantId && taken.has(String(entry.candidate.dtcVariantId));
      // What choosing an already-owned bottle saves: this step's own product,
      // unless that product is itself shared with another step (then nothing
      // leaves the cart and the swap is cost-neutral).
      entry.sharedSaves = entry.taken && !currentShared && current.priceMsrp ? current.priceMsrp : 0;
    });
    return list;
  }

  function sharedNote(entry, short) {
    var saves = entry.sharedSaves ? ' · saves ' + money(entry.sharedSaves) : '';
    return short
      ? 'Also in routine' + saves
      : 'Already in another step — one bottle covers both' + saves;
  }

  function deltaNote(candidate, current) {
    if (candidate.priceMsrp === null || candidate.priceMsrp === undefined) return '';
    if (current.priceMsrp === null || current.priceMsrp === undefined) return '';
    var delta = candidate.priceMsrp - current.priceMsrp;
    if (delta === 0) return 'Same price';
    return (delta > 0 ? '+' : '−') + money(Math.abs(delta)) + ' vs current';
  }

  function productHref(p) {
    if (p.url) return p.url;
    if (p.handle) return '/products/' + String(p.handle).replace(/^\/+/, '');
    return null;
  }

  /* Swap-card tag (prototype v2.3 TAGLABEL/TAGSTYLE). The prototype hardcoded
     a tag per demo product; ours must be DERIVED from real data or omitted —
     never invented:
       sale    — the variant has a genuine compare_at_price above its price
                 (the API only sends compareAt when compare_at > price), so the
                 percentage is real. Highest priority: it is the only tag that
                 states a fact about money.
       budget  — cheapest of the alternatives actually offered for this slot.
       premium — most expensive of those alternatives.
     "Derm pick" is deliberately NOT used here: every product in the routine is
     dermatologist-selected, so as a way to tell one alternative from another it
     would carry no information. Middle-priced alternatives get no tag, which
     matches the prototype (not every card is tagged). */
  var TAG_STYLE = {
    sale: 'background:#fdeaea;color:#a33',
    budget: 'background:#EAF3EC;color:#2c6b3f',
    premium: 'background:#efeaf8;color:#5a3d8f'
  };

  function candidateTag(c, allCandidates) {
    if (c.compareAt && c.priceMsrp && c.compareAt > c.priceMsrp) {
      var pct = Math.round(((c.compareAt - c.priceMsrp) / c.compareAt) * 100);
      if (pct > 0) return { key: 'sale', label: pct + '% off' };
    }
    var prices = [];
    allCandidates.forEach(function (x) {
      if (typeof x.priceMsrp === 'number' && isFinite(x.priceMsrp)) prices.push(x.priceMsrp);
    });
    // A min/max is only meaningful with at least 3 options to rank.
    if (prices.length < 3 || typeof c.priceMsrp !== 'number') return null;
    var min = Math.min.apply(null, prices);
    var max = Math.max.apply(null, prices);
    if (min === max) return null;
    if (c.priceMsrp === min) return { key: 'budget', label: 'Budget pick' };
    if (c.priceMsrp === max) return { key: 'premium', label: 'Premium' };
    return null;
  }

  function renderDrawer(view) {
    var overlay = S.els['drawer-overlay'];
    var drawer = S.root.querySelector('[data-slq-drawer]');
    if (!S.drawerKey) { overlay.hidden = true; drawer.hidden = true; return; }
    var g = null;
    view.groups.forEach(function (x) { if (x.key === S.drawerKey) g = x; });
    if (!g) { S.drawerKey = null; overlay.hidden = true; drawer.hidden = true; return; }

    overlay.hidden = false;
    drawer.hidden = false;
    S.els['drawer-title'].textContent = (categoryForSlot(g.step.slot) || 'Product') + ' Options Right For You';
    S.els['drawer-subnote'].textContent = 'Scroll to compare. Choose one only if you prefer it over your match.'
      + (g.time === 'BOTH' ? ' This product is used morning and evening, so your pick updates both steps.' : '');

    var wrap = S.els['drawer-cards'];
    wrap.innerHTML = '';
    var entries = candidateList(g, view);
    entries.forEach(function (entry) {
      var c = entry.candidate;
      var node = clone('drawer-card');
      var card = node; // template root IS the card ([data-slq-el="card"] on the root)
      var img = tel(node, 'img');
      if (c.image) img.src = c.image; else img.style.visibility = 'hidden';
      img.alt = c.title || '';
      var href = productHref(c);
      var imgLink = tel(node, 'img-link');
      var nameLink = tel(node, 'name-link');
      nameLink.textContent = c.title || '';
      if (href) { imgLink.href = href; nameLink.href = href; }
      else { imgLink.removeAttribute('href'); nameLink.removeAttribute('href'); }
      tel(node, 'price').textContent = priceText(c.priceMsrp);
      var was = tel(node, 'was');
      if (was) {
        var onSale = c.compareAt && c.priceMsrp && c.compareAt > c.priceMsrp;
        was.hidden = !onSale;
        if (onSale) was.textContent = priceText(c.compareAt);
      }
      var tagEl = tel(node, 'tag');
      if (tagEl) {
        var tag = candidateTag(c, entries.map(function (e) { return e.candidate; }));
        tagEl.hidden = !tag;
        if (tag) {
          tagEl.textContent = tag.label;
          tagEl.style.cssText = 'font-size:10.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;border-radius:6px;padding:3px 8px;' + TAG_STYLE[tag.key];
        }
      }
      tel(node, 'oos').hidden = c.inStock !== false;
      var note = tel(node, 'note');
      var pick = tel(node, 'pick');
      if (entry.isCurrent) {
        note.textContent = 'Currently in your routine';
        pick.textContent = '✓ Selected';
        pick.style.cssText = 'border-radius:999px;font-size:12.5px;font-weight:700;padding:8px 16px;flex:none;font-family:inherit;background:var(--slq-type-deep);border:1.5px solid var(--slq-type-deep);color:#fff;pointer-events:none';
        card.style.border = '1.5px solid var(--slq-type-deep)';
        card.style.background = 'var(--slq-type-tint)';
      } else if (entry.taken) {
        note.textContent = sharedNote(entry, false);
        pick.textContent = 'Use here too';
        pick.addEventListener('click', function () { doSwapGroup(g, c); });
      } else {
        note.textContent = entry.isOriginal ? 'Original pick for this step' : deltaNote(c, g.step);
        pick.addEventListener('click', function () { doSwapGroup(g, c); });
      }
      wrap.appendChild(node);
    });
  }

  /* ------------------------------------------------------------------ */
  /* card sub-block renderer (shared desktop + mobile)                   */
  /* ------------------------------------------------------------------ */

  function renderSubBlock(node, step) {
    var box = tel(node, 'sub');
    if (!box) return;
    if (!isBundleAddableStep(step)) { box.hidden = true; return; }
    var opts = plansForStep(step);
    if (opts === null) { ensurePlans(step); box.hidden = true; return; }
    if (!opts.length) { box.hidden = true; return; }
    box.hidden = false;
    var sub = subFor(step);
    var sw = tel(node, 'sub-switch');
    var knob = sw.firstElementChild;
    sw.setAttribute('aria-checked', sub ? 'true' : 'false');
    sw.style.background = sub ? 'var(--slq-type-deep)' : '#DBDCDD';
    knob.style.left = sub ? '23px' : '3px';
    var select = tel(node, 'sub-select');
    select.innerHTML = '';
    var usedPlanLabels = {};
    opts.forEach(function (o) {
      var opt = document.createElement('option');
      opt.value = String(o.id);
      // v6: marketed rhythms read Monthly/Bimonthly here too. A product can
      // carry TWO plans for the same rhythm (Zerafite: legacy "every 1 month"
      // + "every 30 days") - the duplicate keeps its verbatim vendor name so
      // no plan is hidden and no two options read the same.
      var planLabel = cadenceMarketingName(o.intervalDays) || o.name;
      if (usedPlanLabels[planLabel]) planLabel = o.name;
      usedPlanLabels[planLabel] = true;
      opt.textContent = planLabel;
      select.appendChild(opt);
    });
    if (sub) select.value = String(sub.planId);
    select.hidden = !sub;
    var priceEl = tel(node, 'sub-price');
    if (sub && sub.priceCents !== null && sub.priceCents !== undefined) {
      priceEl.hidden = false;
      // Phone card: switch | Subscribe & Save | price on ONE row, the cadence
      // select below — "$49.30/delivery" keeps the price from wrapping there.
      priceEl.textContent = moneyCents(sub.priceCents) + (isIngSheetMode() ? '/delivery' : ' per delivery');
    } else priceEl.hidden = true;
    sw.addEventListener('click', function () { setSubscription(step, !subFor(step), select.value); });
    select.addEventListener('change', function () { if (subFor(step)) setSubscription(step, true, select.value); });
  }

  /* ------------------------------------------------------------------ */
  /* renderers                                                           */
  /* ------------------------------------------------------------------ */

  function glanceRowFill(node, g, small) {
    var step = g.step;
    var img = tel(node, 'img');
    if (step.image) img.src = step.image; else img.style.visibility = 'hidden';
    img.alt = step.title || '';
    tel(node, 'name').textContent = step.title || labelForGroup(g);
    if (!small) {
      tel(node, 'slot').textContent = categoryForSlot(step.slot) || (step.slot || '');
      var ampmEl = tel(node, 'ampm');
      var badge = timeBadgeLabel(g.time);
      if (badge) { ampmEl.hidden = false; ampmEl.textContent = badge; } else ampmEl.hidden = true;
      var oos = tel(node, 'oos');
      if (oos) oos.hidden = step.inStock !== false;
    }
    tel(node, 'price').textContent = isInformationalStep(step) ? '' : priceText(step.priceMsrp);
    if (S.flashKey && g.key === S.flashKey) {
      node.style.borderColor = 'var(--slq-type-deep)';
      node.style.background = 'var(--slq-type-tint)';
    }
    var open = function (e) { e.stopPropagation(); openDrawer(g.key, 'glance'); };
    node.addEventListener('click', open);
    // The control's only text is "×", which assistive tech reads as "times" —
    // name it after the product so the shopper knows what they are removing.
    var rmBtn2 = tel(node, 'remove'); if (rmBtn2) { rmBtn2.hidden = !!g.kit; rmBtn2.setAttribute('aria-label', removeLabelForGroup(g)); rmBtn2.addEventListener('click', function (e) { e.stopPropagation(); removeGroup(g); }); }
  }

  function renderGlanceHero(view) {
    var wrap = S.els['glance-hero'];
    wrap.innerHTML = '';
    activeGroups(view).forEach(function (g) {
      var node = clone('glance-row');
      glanceRowFill(node, g, false);
      wrap.appendChild(node);
    });
  }

  function renderGlanceAside(view) {
    var wrap = S.els['glance-aside'];
    wrap.innerHTML = '';
    activeGroups(view).filter(function (g) { return groupPassesTime(g, S.glanceTab); }).forEach(function (g) {
      var node = clone('glance-aside-row');
      glanceRowFill(node, g, true);
      wrap.appendChild(node);
    });
  }

  /* Ingredient chip + hover/tap info popover (prototype v2.3). Copy comes from
     the regimen payload's ingredientInfo map (the DTC key_ingredients
     metaobjects, mirrored + snippet-sized by the API); an ingredient with no
     entry — "paraben-free" and friends — renders as a plain chip with no
     popover and no cursor affordance, rather than an empty tooltip. */
  function ingredientInfoFor(name) {
    if (!S.ingredientInfo || !name) return null;
    return S.ingredientInfo[String(name).trim().toLowerCase()] || null;
  }

  /* The popover is a child of the chip wrapper, and the chip wrapper sits inside
     the two-row clamp below (max-height + overflow:hidden). An absolutely
     positioned popover that opens ABOVE its chip therefore lands entirely
     outside the clamped box and is clipped to nothing — the popover "opened"
     (hidden=false) on every hover and tap and nobody ever saw it. Positioning
     it fixed, from the chip's live rect, takes it out of that overflow box
     without moving it in the DOM (events, template and close-button stay put).
     Above the chip by default; below when there is no room. Fixed coordinates
     go stale on scroll, so any scroll/resize simply closes open popovers. */
  var ING_POP_GAP = 8;
  var ING_POP_EDGE = 8;
  /* Phones: a 250px bubble anchored to a chip is cramped, lands under the
     thumb, and vanished on the first scroll (fixed coords go stale). Under
     760px the popover is a bottom sheet instead — full width, sitting just
     above the sticky bar, titled with the ingredient (CSS ::before reads
     data-slq-ing-title), dismissed by ×, an outside tap or another chip, and
     NOT by scrolling (its position never depended on the chip). */
  function isIngSheetMode() {
    try { return !!(window.matchMedia && window.matchMedia('(max-width: 760px)').matches); } catch (e) { return false; }
  }
  function positionIngPop(chipEl, pop) {
    if (!chipEl || !pop) return;
    if (isIngSheetMode()) {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var bar = S && S.root ? S.root.querySelector('[data-slq-sticky-bar]') : null;
      var bb = (bar && getComputedStyle(bar).display !== 'none') ? bar.getBoundingClientRect() : null;
      var bottom = (bb && bb.top > 0 && bb.top < vh) ? Math.round(vh - bb.top + 10) : 12;
      pop.setAttribute('data-slq-ing-sheet', '1');
      pop.style.position = 'fixed';
      pop.style.left = '12px';
      pop.style.right = '12px';
      pop.style.width = 'auto';
      pop.style.top = 'auto';
      pop.style.bottom = bottom + 'px';
      pop.style.zIndex = '80';
      return;
    }
    pop.removeAttribute('data-slq-ing-sheet');
    pop.style.right = 'auto';
    pop.style.width = '';
    var r = chipEl.getBoundingClientRect();
    pop.style.position = 'fixed';
    pop.style.bottom = 'auto';
    pop.style.zIndex = '80';
    var w = pop.offsetWidth || 250;
    var h = pop.offsetHeight || 60;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var left = Math.max(ING_POP_EDGE, Math.min(r.left, vw - w - ING_POP_EDGE));
    var top = r.top - h - ING_POP_GAP;
    if (top < ING_POP_EDGE) top = r.bottom + ING_POP_GAP;
    pop.style.left = Math.round(left) + 'px';
    pop.style.top = Math.round(top) + 'px';
  }

  /* Key-ingredient lists run long — a barrier cream can carry a dozen chips,
     which buried the "Swap item" control under a wall of pills. Show two rows
     and put the rest behind a toggle.

     Rows are measured, not guessed: chips are variable width and wrap, so the
     only honest way to find the second row's bottom is to group the rendered
     chips by offsetTop after layout. That also means this has to re-run on
     resize, since a narrower viewport rewraps them. */
  var ING_MAX_ROWS = 2;
  var ING_GAP = 8;        // matches the wrapper's gap below
  var ING_ROW_FALLBACK = 31;  // chip: 12.5px text + 5px padding + 1px border

  /* The clamp is applied UNCONDITIONALLY, and only the button's visibility
     depends on a measurement. That ordering matters: these cards frequently
     render while collapsed or inside a display:none column, where every
     measurement reads 0 — if the clamp itself were conditional, a 12-chip list
     would render fully expanded and never recover.

     Deliberately no ResizeObserver here. RO callbacks are delivered on the
     rendering steps, and those stall in a backgrounded/duplicated renderer —
     measured 0 callbacks in 700ms on a laid-out element while debugging this.
     scrollHeight/clientHeight are synchronous and always truthful, so the
     button is decided from those and re-checked on re-render and on resize. */
  function clampIngRows(wrap, btn) {
    if (!wrap || !btn) return;
    if (wrap.getAttribute('data-slq-ings-open') === '1') return; // expanded by the visitor
    var first = wrap.firstElementChild;
    var rowH = (first && first.offsetHeight) || ING_ROW_FALLBACK;
    wrap.style.maxHeight = (ING_MAX_ROWS * rowH + (ING_MAX_ROWS - 1) * ING_GAP) + 'px';
    wrap.style.overflow = 'hidden';
    btn.hidden = !(wrap.scrollHeight > wrap.clientHeight + 1);
    btn.textContent = 'Show all ingredients';
  }

  function reclampAllIngRows() {
    if (!S || !S.root) return;
    S.root.querySelectorAll('[data-slq-ings]').forEach(function (wrap) {
      clampIngRows(wrap, wrap.nextElementSibling);
    });
  }

  function renderIngChips(container, step) {
    /* Chips go in their own wrapper so the clamp never hides the badges that
       share the row (DERM PICK, in-stock substitute, the AM+PM note). */
    var wrap = document.createElement('div');
    wrap.setAttribute('data-slq-ings', '');
    wrap.style.cssText = 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;width:100%';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.hidden = true;
    btn.style.cssText = 'align-self:flex-start;margin-top:7px;background:none;border:none;padding:0;'
      + 'font-family:inherit;font-size:12.5px;font-weight:700;color:var(--slq-type-deep,#2F3572);'
      + 'text-decoration:underline;cursor:pointer';
    btn.addEventListener('click', function () {
      var open = wrap.getAttribute('data-slq-ings-open') === '1';
      if (open) {
        wrap.removeAttribute('data-slq-ings-open');
        clampIngRows(wrap, btn);
      } else {
        wrap.setAttribute('data-slq-ings-open', '1');
        wrap.style.maxHeight = '';
        wrap.style.overflow = '';
        btn.textContent = 'Show fewer ingredients';
      }
    });

    container.appendChild(wrap);
    container.appendChild(btn);
    renderIngChipsInto(wrap, step);
    clampIngRows(wrap, btn);
    /* Second pass on the next macrotask. The clamp above frequently runs while
       the card is still collapsed, where clientHeight/scrollHeight are both 0
       and the overflow test wrongly concludes "fits, no button needed" — the
       clamp itself is fine, only the button's visibility is wrong. A timer
       re-checks once layout exists. Deliberately a timer and not rAF: the
       render steps stall in a backgrounded renderer, timers do not. */
    setTimeout(function () { clampIngRows(wrap, btn); }, 0);
  }

  function renderIngChipsInto(container, step) {
    (step.keyIngredients || []).forEach(function (ing) {
      if (!ing) return;
      var chip = clone('ing-chip');
      tel(chip, 'label').textContent = ing;
      var info = ingredientInfoFor(ing);
      var pop = tel(chip, 'pop');
      // clone() hands back the template's firstElementChild, which IS the wrap
      // span — and querySelector only searches DESCENDANTS, so tel() can never
      // find it. Match self-or-descendant.
      var wrap = chip.getAttribute('data-slq-el') === 'wrap' ? chip : tel(chip, 'wrap');
      if (info && pop && wrap) {
        tel(chip, 'pop-text').textContent = info;
        pop.setAttribute('data-slq-ing-title', String(ing));
        wrap.style.cursor = 'pointer';
        var chipEl = tel(chip, 'chip');
        var show = function () {
          closeAllIngPops();
          pop.hidden = false;
          positionIngPop(chipEl || wrap, pop);
        };
        var hide = function () { pop.hidden = true; pop.removeAttribute('data-slq-ing-pinned'); };
        var isPinned = function () { return pop.getAttribute('data-slq-ing-pinned') === '1'; };
        // Pointer: hover previews, and a click PINS the popover open so moving
        // the mouse away (or towards the popover itself) does not dismiss it.
        // Previously click was a plain toggle, so on a mouse the click landed
        // on an already-hover-opened popover and CLOSED it — "clicking the
        // ingredient does nothing". Clicking a pinned chip again, tapping the
        // close button (shown by CSS at mobile widths), or clicking anywhere
        // else (document listener in bindStatic) dismisses it.
        wrap.addEventListener('mouseenter', function () { if (pop.hidden) show(); });
        wrap.addEventListener('mouseleave', function () { if (!isPinned()) hide(); });
        wrap.addEventListener('click', function (e) {
          e.stopPropagation();
          if (!pop.hidden && isPinned()) { hide(); return; }
          show();
          pop.setAttribute('data-slq-ing-pinned', '1');
        });
        var closeBtn = tel(chip, 'pop-close');
        if (closeBtn) closeBtn.addEventListener('click', function (e) { e.stopPropagation(); hide(); });
        if (chipEl) {
          chipEl.setAttribute('tabindex', '0');
          chipEl.addEventListener('focus', show);
          chipEl.addEventListener('blur', function () { if (!isPinned()) hide(); });
        }
      } else if (pop) {
        pop.remove();
      }
      container.appendChild(chip);
    });
  }

  function closeAllIngPops() {
    // Guarded: this now runs from a document-level listener, which outlives any
    // single instance.
    if (!S || !S.root) return;
    S.root.querySelectorAll('[data-slq-ing-pop]').forEach(function (p) {
      p.hidden = true;
      p.removeAttribute('data-slq-ing-pinned');
    });
  }

  function renderProtocol(view) {
    var wrap = S.els['protocol-list'];
    wrap.innerHTML = '';
    if (view.kitMode && !view.groups.length) {
      var note = document.createElement('div');
      note.style.cssText = 'background:#fff;border:1px solid #DBDCDD;border-radius:18px;padding:26px;text-align:center;font-size:14.5px;color:#6E7076';
      note.textContent = view.kitError
        ? 'We couldn’t load the Bundle for your type right now — the 3–4 and 5+ step regimens above are unaffected.'
        : 'Loading your Bundle…';
      wrap.appendChild(note);
      return;
    }
    var shown = activeGroups(view).filter(function (g) { return groupPassesTime(g, S.tod); });
    shown.forEach(function (g, idx) {
      var step = g.step;
      var node = clone('protocol-card');
      tel(node, 'step-num').textContent = String(idx + 1);
      var img = tel(node, 'img');
      if (step.image) img.src = step.image; else img.style.visibility = 'hidden';
      img.alt = step.title || '';
      var href = productHref(step);
      var imgLink = tel(node, 'img-link');
      var titleLink = tel(node, 'title-link');
      titleLink.textContent = step.title || labelForGroup(g);
      if (href) { imgLink.href = href; titleLink.href = href; }
      else { imgLink.removeAttribute('href'); titleLink.removeAttribute('href'); }
      tel(node, 'cat').textContent = categoryForSlot(step.slot) || (step.slot || '');
      var ampmEl = tel(node, 'ampm');
      var badge = timeBadgeLabel(g.time);
      if (badge) { ampmEl.hidden = false; ampmEl.textContent = badge; } else ampmEl.hidden = true;

      // mobile collapse state
      var isOpen = !!S.cardOpen[g.key];
      var closedFlag = isOpen ? '0' : '1';
      var detail = tel(node, 'detail');
      detail.setAttribute('data-slq-closed', closedFlag);
      var collapsedPrice = tel(node, 'collapsed-price');
      collapsedPrice.setAttribute('data-slq-closed', closedFlag);
      collapsedPrice.textContent = isInformationalStep(step) ? '' : priceText(step.priceMsrp);
      var toggle = tel(node, 'detail-toggle');
      toggle.innerHTML = isOpen ? 'Hide details ▴' : 'See more details ▾';
      toggle.addEventListener('click', function () {
        S.cardOpen[g.key] = !isOpen;
        scheduleRender();
      });

      // prototype parity: a short consumer description per category, never
      // the raw INCI dump (key ingredients stay as chips below)
      var summary = tel(node, 'ing-summary');
      var copy = categoryCopy(step);
      if (copy) { summary.hidden = false; summary.textContent = copy; }
      else summary.hidden = true;
      renderIngChips(tel(node, 'chips'), step);
      var pickBadge = tel(node, 'pick-badge');
      if (pickBadge) {
        pickBadge.hidden = false;
        pickBadge.textContent = g.kit ? 'CURATED KIT' : 'DERM PICK';
      }
      tel(node, 'both-badge').hidden = g.time !== 'BOTH';
      tel(node, 'substituted').hidden = step.substituted !== true;
      tel(node, 'oos').hidden = step.inStock !== false;

      renderSubBlock(node, step);

      var validCandidates = (step.swapCandidates || []).filter(isValidSwapCandidate)
        .filter(function (c) { return !step.dtcVariantId || String(c.dtcVariantId) !== String(step.dtcVariantId); });
      var swapBtn = tel(node, 'swap-btn');
      if (validCandidates.length) {
        swapBtn.hidden = false;
        // Stamp the group key so closeDrawer() can find this control again
        // after the re-render and return focus to it.
        swapBtn.setAttribute('data-slq-swap-for', g.key);
        tel(node, 'swap-count').textContent = String(validCandidates.length);
        swapBtn.addEventListener('click', function () { openDrawer(g.key, 'protocol'); });
      } else swapBtn.hidden = true;

      tel(node, 'price').textContent = isInformationalStep(step) ? '' : priceText(step.priceMsrp);
      var rmBtn = tel(node, 'remove'); if (rmBtn) { rmBtn.hidden = !!g.kit; rmBtn.setAttribute('aria-label', removeLabelForGroup(g)); rmBtn.addEventListener('click', function () { removeGroup(g); }); }
      wrap.appendChild(node);
    });
  }

  function renderRemoved(view) {
    var removed = removedGroups(view);
    var panel = S.els['removed-panel'];
    var list = S.els['removed-list'];
    var mobPanel = S.els['mob-removed'];
    var mobList = S.els['mob-removed-list'];
    list.innerHTML = '';
    mobList.innerHTML = '';
    panel.hidden = removed.length === 0;
    mobPanel.hidden = removed.length === 0;
    removed.forEach(function (g) {
      var node = clone('removed-row');
      var img = tel(node, 'img');
      if (g.step.image) img.src = g.step.image; else img.style.visibility = 'hidden';
      tel(node, 'name').textContent = g.step.title || labelForGroup(g);
      tel(node, 'slot').textContent = categoryForSlot(g.step.slot) || (g.step.slot || '');
      tel(node, 'add-back').addEventListener('click', function () { addBackGroup(g); });
      list.appendChild(node);

      var mnode = clone('mob-removed-row');
      var mimg = tel(mnode, 'img');
      if (g.step.image) mimg.src = g.step.image; else mimg.style.visibility = 'hidden';
      tel(mnode, 'name').textContent = g.step.title || labelForGroup(g);
      tel(mnode, 'add-back').addEventListener('click', function () { addBackGroup(g); });
      mobList.appendChild(mnode);
    });
  }

  function renderMob(view) {
    var wrap = S.els['mob-list'];
    wrap.innerHTML = '';
    var shown = activeGroups(view).filter(function (g) { return groupPassesTime(g, S.tod); });
    shown.forEach(function (g, idx) {
      var step = g.step;
      var node = clone('mob-step');
      var isOpen = S.mobOpen === g.key;
      node.style.border = '1.5px solid ' + (isOpen ? 'var(--slq-type-deep)' : '#E4E4DF');
      if (isOpen) node.style.boxShadow = '0 12px 32px rgba(15,18,50,.12)';
      var header = tel(node, 'header');
      header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      tel(node, 'step-num').textContent = String(idx + 1);
      var img = tel(node, 'img');
      if (step.image) img.src = step.image; else img.style.visibility = 'hidden';
      img.alt = step.title || '';
      tel(node, 'cat').textContent = categoryForSlot(step.slot) || (step.slot || '');
      var ampmEl = tel(node, 'ampm');
      var badge = timeBadgeLabel(g.time);
      if (badge) { ampmEl.hidden = false; ampmEl.textContent = badge; } else ampmEl.hidden = true;
      var titleLink = tel(node, 'title-link');
      titleLink.textContent = step.title || labelForGroup(g);
      var href = productHref(step);
      var imgLink = tel(node, 'img-link');
      if (href && isOpen) { imgLink.href = href; titleLink.href = href; }
      else { imgLink.removeAttribute('href'); titleLink.removeAttribute('href'); }
      tel(node, 'price').textContent = isInformationalStep(step) ? '' : priceText(step.priceMsrp);
      tel(node, 'chevron').innerHTML = isOpen ? '▴' : '▾';
      header.addEventListener('click', function () {
        S.mobOpen = isOpen ? null : g.key;
        scheduleRender();
      });
      // when open, product links navigate without collapsing the card
      [imgLink, titleLink].forEach(function (a) {
        a.addEventListener('click', function (e) {
          if (isOpen) e.stopPropagation();
          else e.preventDefault();
        });
      });

      var detail = tel(node, 'detail');
      detail.hidden = !isOpen;
      if (isOpen) {
        tel(node, 'substituted').hidden = step.substituted !== true;
        tel(node, 'oos').hidden = step.inStock !== false;
        var summary = tel(node, 'ing-summary');
        var mcopy = categoryCopy(step);
        if (mcopy) { summary.hidden = false; summary.textContent = mcopy; }
        else summary.hidden = true;
        renderIngChips(tel(node, 'chips'), step);
        renderSubBlock(node, step);

        var entries = candidateList(g, view);
        var altsWrap = tel(node, 'alts');
        altsWrap.innerHTML = '';
        var swappable = entries.filter(function (e2) { return !e2.isCurrent; });
        var head = tel(node, 'swap-head');
        head.hidden = swappable.length === 0;
        // null-guarded: the storefront page cache serves the OLD template for
        // minutes after a deploy, and new JS must never crash on old markup
        var shTitle = tel(node, 'swap-head-title');
        if (shTitle) shTitle.textContent = (categoryForSlot(step.slot) || 'Product') + ' Options Right For You';
        tel(node, 'alt-label').textContent = swappable.length + (swappable.length === 1 ? ' option' : ' options');
        entries.forEach(function (entry) {
          var c = entry.candidate;
          var tile = clone('mob-alt');
          var timg = tel(tile, 'img');
          if (c.image) timg.src = c.image; else timg.style.visibility = 'hidden';
          tel(tile, 'name').textContent = c.title || '';
          tel(tile, 'price').textContent = priceText(c.priceMsrp);
          var noteEl = tel(tile, 'note');
          if (entry.isCurrent) {
            noteEl.textContent = '✓ In routine';
            noteEl.style.color = 'var(--slq-type-deep)';
            tile.style.border = '2px solid var(--slq-type-deep)';
            tile.style.boxShadow = '0 6px 18px rgba(47,53,114,.14)';
          } else if (entry.taken) {
            noteEl.textContent = sharedNote(entry, true);
            noteEl.style.color = 'var(--slq-type-deep)';
            tile.addEventListener('click', function () { doSwapGroup(g, c); });
          } else {
            noteEl.textContent = entry.isOriginal ? 'Original pick' : (deltaNote(c, g.step).replace(' vs current', '') || '');
            tile.addEventListener('click', function () { doSwapGroup(g, c); });
          }
          altsWrap.appendChild(tile);
        });

        var rmBtn = tel(node, 'remove'); if (rmBtn) { rmBtn.hidden = !!g.kit; rmBtn.addEventListener('click', function () { removeGroup(g); }); }
      }
      wrap.appendChild(node);
    });

    var n = shown.length;
    S.els['mob-summary'].textContent = n + (n === 1 ? ' step' : ' steps') +
      (S.tod === 'am' ? ' · morning' : S.tod === 'pm' ? ' · evening' : ' · morning + evening');
  }

  function renderTotals(view) {
    var t = computeTotals(view);
    var was = t.savings > 0.005 ? money(t.msrp) : '';
    var now = money(t.now);
    var pairs = [
      ['hero-total-was', 'hero-total-now'],
      ['plain-total-was', 'plain-total-now'],
      ['glance-hero-was', 'glance-hero-now'],
      ['aside-total-was', 'aside-total-now'],
      ['mob-total-was', 'mob-total-now'],
      ['sb-was', 'sb-now']
    ];
    pairs.forEach(function (p) {
      if (S.els[p[0]]) S.els[p[0]].textContent = was;
      if (S.els[p[1]]) S.els[p[1]].textContent = now;
    });
    var save = S.els['sb-save'];
    if (save) save.textContent = t.savings > 0.005 ? 'You save ' + money(t.savings) + ' every refill' : 'One-time purchase';
  }

  function renderSegs(view) {
    var segDark = function (on) {
      return 'border-radius:999px;padding:8px 12px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;white-space:nowrap;flex:1 1 auto;text-align:center;' +
        (on ? 'background:var(--slq-type-bright);border:1px solid var(--slq-type-bright);color:#2F3572'
            : 'background:transparent;border:1px solid rgba(255,255,255,.45);color:#cfd3ea');
    };
    var segLight = function (on) {
      return 'border-radius:999px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;font-family:inherit;' +
        (on ? 'background:#2F3572;border:1px solid #2F3572;color:#fff'
            : 'background:transparent;border:1px solid #DBDCDD;color:#6E7076');
    };
    var todStyle = function (on) {
      return 'border-radius:999px;padding:11px 30px;font-size:14.5px;font-weight:600;border:none;cursor:pointer;font-family:inherit;' +
        (on ? 'background:#2F3572;color:#fff' : 'background:transparent;color:#6E7076');
    };
    var glanceStyle = function (on) {
      return 'font-family:inherit;font-size:11px;font-weight:700;padding:4px 0;border-radius:999px;cursor:pointer;flex:1;text-align:center;' +
        (on ? 'background:var(--slq-type-deep);color:#fff;border:1px solid var(--slq-type-deep)'
            : 'background:#fff;color:#6E7076;border:1px solid #DBDCDD');
    };
    var mobTabStyle = function (on) {
      return 'flex:1;border-radius:999px;padding:11px 0;font-size:13px;font-weight:700;border:none;cursor:pointer;font-family:inherit;white-space:nowrap;' +
        (on ? 'background:var(--slq-type-deep);color:#fff' : 'background:transparent;color:#2F3572');
    };
    var all = S.root.querySelectorAll('[data-slq-seg]');
    var showKit = kitAvailable();
    var sizesDiffer = regimenSizesDiffer();
    for (var i = 0; i < all.length; i++) {
      var b = all[i];
      var seg = b.getAttribute('data-slq-seg');
      var on = seg === S.size;
      // cssText replaces every inline style, so the hide has to ride along.
      // Only "5+" goes when the sizes are identical — never "3-4" as well. The
      // Bundle lives in this same control, so hiding both routine options would
      // leave a lone Bundle button with no way back to the routine. Keeping
      // "3-4 Step Regimen" is also honest: a hypoallergenic routine really is
      // ~4 products (cleanser, eye, moisturiser, SPF). When there is no Bundle
      // either, hideSizeControl below drops the whole control anyway.
      var hideSeg = (seg === 'kit' && !showKit) ||
        (seg === 'plus' && !sizesDiffer);
      b.style.cssText = (b.hasAttribute('data-slq-seg-dark') ? segDark(on) : segLight(on)) +
        (hideSeg ? ';display:none' : '');
      // These are segmented controls: without aria-pressed the selected option
      // is conveyed by colour alone, so assistive tech announces every option
      // identically and the shopper cannot tell which regimen size is active.
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    var tods = S.root.querySelectorAll('[data-slq-tod]');
    for (var j = 0; j < tods.length; j++) {
      var todOn = tods[j].getAttribute('data-slq-tod') === S.tod;
      tods[j].style.cssText = todStyle(todOn);
      tods[j].setAttribute('aria-pressed', todOn ? 'true' : 'false');
    }
    var gtabs = S.root.querySelectorAll('[data-slq-glance-tab]');
    for (var k = 0; k < gtabs.length; k++) {
      var gOn = gtabs[k].getAttribute('data-slq-glance-tab') === S.glanceTab;
      gtabs[k].style.cssText = glanceStyle(gOn);
      gtabs[k].setAttribute('aria-pressed', gOn ? 'true' : 'false');
    }
    var mtabs = S.root.querySelectorAll('[data-slq-mob-tab]');
    for (var m = 0; m < mtabs.length; m++) {
      mtabs[m].style.cssText = mobTabStyle(mtabs[m].getAttribute('data-slq-mob-tab') === S.tod);
    }
    // the kit has no AM/PM split — hide the time-of-day segment in kit view
    var todSeg = S.root.querySelector('[data-slq-tod-seg]');
    if (todSeg) todSeg.style.display = view && view.kitMode ? 'none' : '';

    // With core and plus both hidden the control has nothing left to offer
    // unless the Bundle is there, so drop the whole thing and its heading —
    // otherwise the page shows an empty pill row under "Choose your regimen
    // size". The mobile copy is display:none until its own media query turns it
    // on, so only ever force it OFF here; never force it visible.
    var hideSizeControl = !sizesDiffer && !showKit;
    var segWraps = S.root.querySelectorAll('[data-slq-size-seg]');
    for (var s = 0; s < segWraps.length; s++) {
      if (hideSizeControl) segWraps[s].style.display = 'none';
      else segWraps[s].style.removeProperty('display');
    }
    var segLabels = S.root.querySelectorAll('[data-slq-size-label]');
    for (var t = 0; t < segLabels.length; t++) {
      if (hideSizeControl) segLabels[t].style.display = 'none';
      else segLabels[t].style.removeProperty('display');
    }
  }

  /* The "3-4 Step Regimen" / "5+ Step Regimen" choice is only real when there
     is a complete-only step for the 5+ view to add. A hypoallergenic routine is
     built entirely from HA slots, and widget/regimen.ts promotes the whole
     routine to `starter` when nothing else would tier that way (so the page
     never opens on an empty view — the 2026-08-10 incident). The side effect is
     that BOTH sizes then resolve to the identical set: Max Baumann's DSNT
     routine on 2026-08-13 was 7 HA steps, and 3-4 and 5+ showed the same seven.
     The routine is right; offering a choice that changes nothing is not. */
  function regimenSizesDiffer() {
    var steps = (S && S.steps) || [];
    return steps.some(function (st) { return st && st.tier === 'complete'; });
  }

  function renderStickyBar(view) {
    var bar = S.root.querySelector('[data-slq-sticky-bar]');
    if (!bar) return;
    bar.style.display = S.sbVisible ? 'block' : 'none';
    var body = bar.querySelector('[data-slq-sb-body]');
    body.setAttribute('data-slq-closed', S.sbOpen ? '0' : '1');
    body.style.display = S.sbOpen ? 'flex' : 'none';
    S.els['sb-collapsed'].hidden = S.sbOpen;
    S.els['sb-min'].hidden = !S.sbOpen;

    var thumbs = S.els['sb-thumbs'];
    thumbs.innerHTML = '';
    activeGroups(view).forEach(function (g) {
      if (!g.step.image) return;
      var node = clone('sb-thumb'); // template root IS the <img>
      node.src = g.step.image;
      node.alt = g.step.title || '';
      thumbs.appendChild(node);
    });
    S.els['sb-count'].textContent = activeGroups(view).length + ' products';

    // master Subscribe & Save switches (hero + sticky bar) — only when REAL plans exist
    var st = masterSubState(view);
    ['sb-sub', 'master-sub'].forEach(function (name) {
      var box = S.els[name];
      if (box) box.hidden = !st.available;
    });
    // plain hero total only when no product carries a subscription option
    if (S.els['plain-total']) S.els['plain-total'].hidden = st.available;
    var setSwitch = function (name, onBg, offBg) {
      var sw = S.els[name];
      if (!sw) return;
      sw.setAttribute('aria-checked', st.on ? 'true' : 'false');
      sw.style.background = st.on ? onBg : offBg;
      sw.firstElementChild.style.left = st.on ? '23px' : '3px';
    };
    setSwitch('sb-sub-switch', 'var(--slq-type-deep)', '#DBDCDD');
    setSwitch('master-sub-switch', 'var(--slq-type-bright)', 'rgba(255,255,255,.25)');
    renderMasterSubDetails(view, st);
    // Same offer, second surface (prototype parity: the bottom bar carries the
    // percentage and cadence pills, not just a bare "Subscribe & Save").
    renderMasterSubDetails(view, st, { pct: 'sb-sub-pct', note: null, cad: 'sb-cadence', dark: false });
    reserveStickyBarSpace(bar);
  }

  /* The bar is position:fixed, so nothing in the page flow makes room for it.
     On a phone it is always shown and wraps to two or three rows (the
     Subscribe & Save toggle, price + button, the $10 note: ~150-200px), and it
     sat over the end of the page — the last proof image / the bottom of the
     final card could not be scrolled out from under it (Luis, 2026-08-18).
     Reserve the bar's REAL height at the bottom of the results root, measured
     on every bar render (open/closed, plans arriving, resize). Desktop is left
     alone: the bar is one row there and the proof section already carries
     190px for it. */
  function reserveStickyBarSpace(bar) {
    try {
      var root = S && S.root;
      if (!root) return;
      var shown = !!bar && S.sbVisible && getComputedStyle(bar).display !== 'none';
      if (!shown || !isIngSheetMode()) { root.style.paddingBottom = ''; return; }
      var h = Math.ceil(bar.getBoundingClientRect().height);
      /* The page already ends in some breathing room meant for the bar (the
         proof section's 190px bottom padding, or the bare 120px spacer when
         there are no proof images). Only add what the bar needs BEYOND that,
         so the last thing on the page clears the bar by ~12px instead of by a
         screenful. Walk down the last-rendered-child chain: the first bottom
         padding found is that room; a childless block at the end IS the room. */
      var slack = 0, el = root;
      for (var depth = 0; depth < 6 && el; depth++) {
        var next = null;
        for (var i = el.children.length - 1; i >= 0; i--) {
          var c = el.children[i];
          if (!c || c === bar || c.tagName === 'TEMPLATE') continue;
          var cs = getComputedStyle(c);
          if (cs.position === 'fixed' || cs.display === 'none' || !c.getBoundingClientRect().height) continue;
          next = c; break;
        }
        if (!next) { slack = el !== root ? el.getBoundingClientRect().height : 0; break; }
        var pb = parseFloat(getComputedStyle(next).paddingBottom) || 0;
        if (pb > 0) { slack = pb; break; }
        el = next;
      }
      var need = Math.max(0, h + 12 - slack);
      root.style.paddingBottom = need > 0 ? need + 'px' : '';
    } catch (e) { /* noop */ }
  }

  /* Hero S&S card details (prototype parity): real % savings + refill note
     when subscription allocations actually discount, and cadence chips built
     from the REAL plan names shared by the plan-bearing products. */
  /* Renders the S&S detail (percentage, savings note, cadence chips) into a
     target. The hero card and the sticky bar show the SAME offer, so they read
     from one implementation — the prototype's bottom bar carries the percentage
     and the cadence pills too, and duplicating this logic is how the two drift
     apart. `dark` selects the on-indigo chip palette used in the hero. */
  function renderMasterSubDetails(view, st, target) {
    target = target || { pct: 'master-sub-pct', note: 'master-sub-note', cad: 'master-cadence', dark: true };
    var pctEl = S.els[target.pct];
    var noteEl = S.els[target.note];
    var cadWrap = S.els[target.cad];
    if (!pctEl && !noteEl && !cadWrap) return;
    // The per-card Subscribe UI (the old lazy-fetch trigger) is gone —
    // kick off the real /products/<handle>.js plan fetches from here so the
    // hero card can appear once plans resolve (scheduleRender on settle).
    unitSteps(view).forEach(function (u) {
      if (isBundleAddableStep(u) && plansForStep(u) === null) ensurePlans(u);
    });
    var bearing = planBearingUnits(view);

    // potential/actual savings across the units the offer applies to by
    // default (cheapest plan per unit). Thin-plan products (Plated, 5%) are
    // left out of the figure the same way they are left out of the default —
    // otherwise one $258 serum at 5% turned "Subscribe & Save 15%" into 8%.
    var msrpSum = 0, subSum = 0, priced = 0;
    defaultSubUnits(view).forEach(function (u) {
      var best = bestPlanFor(u);
      if (best && u.priceMsrp !== null && u.priceMsrp !== undefined) {
        priced++;
        msrpSum += u.priceMsrp;
        subSum += best.priceCents / 100;
      }
    });
    var savings = msrpSum - subSum;
    if (pctEl) pctEl.textContent = (priced && savings > 0.005) ? ' ' + Math.round((savings / msrpSum) * 100) + '%' : '';
    if (noteEl) {
      noteEl.textContent = (priced && savings > 0.005)
        ? 'You save ' + money(savings) + ' every refill'
        : 'Applies to every product with a subscription option. Skip or cancel anytime.';
    }

    if (cadWrap) {
      cadWrap.innerHTML = '';
      if (cadWrap.hasAttribute('hidden')) cadWrap.hidden = !st.on;
      // Offer a cadence only if EVERY subscribable product can actually honour
      // it. Previously the chips were the union of all plan names, so a chip
      // could advertise a rhythm only one product supported and silently leave
      // the rest on whatever plan they already had.
      // Shortest cadence first — shared with the Subscribe & Save default so
      // the chips and the default can never disagree.
      var intervals = commonCadences(bearing);
      // v6: when the catalog supports both marketed rhythms the row is exactly
      // two chips - Monthly and Bimonthly. The in-between 45-day cadence drops
      // from the row (it stays reachable per product via the card's cadence
      // select); if either marketed rhythm is missing, whatever exists renders
      // with its day label instead, so we never invent a plan.
      var marketed = intervals.filter(function (d) { return cadenceMarketingName(d) !== null; });
      if (marketed.length >= 2) intervals = marketed;
      // The chips move the units the offer applies to by default, plus any
      // thin-plan product the shopper opted in by hand — never a thin-plan
      // product they left alone.
      var cadUnits = defaultSubUnits(view).concat(bearing.filter(function (u) { return isThinPlanUnit(u) && !!subFor(u); }));
      intervals.slice(0, 3).forEach(function (days) {
        var active = st.on && cadUnits.length > 0 && cadUnits.every(function (u) {
          var sub = subFor(u);
          return sub && sameCadence(sub.intervalDays, days);
        });
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.textContent = cadenceShopperLabel(days, !target.dark && isIngSheetMode());
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
        chip.style.cssText = 'font-family:inherit;font-size:11px;font-weight:700;border-radius:999px;padding:4px 12px;cursor:pointer;' +
          (target.dark
            ? (active ? 'background:var(--slq-type-bright);border:1px solid var(--slq-type-bright);color:#2F3572'
                      : 'background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.3);color:#fff')
            // sticky bar sits on white: the on-indigo palette is invisible there
            : (active ? 'background:var(--slq-type-deep);border:1px solid var(--slq-type-deep);color:#fff'
                      : 'background:#fff;border:1px solid #DBDCDD;color:#2F3572'));
        chip.addEventListener('click', function () {
          cadUnits.forEach(function (u) {
            var opts = plansForStep(u) || [];
            if (!opts.length) return;
            // Nearest interval wins. Never fall back to opts[0] and never keep
            // an existing plan just because nothing matched by name — either of
            // those silently bills the shopper on a cadence they did not pick.
            var match = planNearest(opts, days);
            if (!match) return;
            S.subs.set(String(u.dtcVariantId), {
              planId: match.id,
              planName: match.name,
              priceCents: match.priceCents,
              intervalDays: match.intervalDays
            });
          });
          scheduleRender();
        });
        cadWrap.appendChild(chip);
      });
    }
  }

  var renderQueued = false;
  function scheduleRender() {
    if (renderQueued) return;
    renderQueued = true;
    window.requestAnimationFrame(function () {
      renderQueued = false;
      renderAll();
    });
  }

  function renderAll() {
    if (!S || S.status !== 'ready') return;
    var view = buildView();
    renderGlanceHero(view);
    renderGlanceAside(view);
    renderProtocol(view);
    renderMob(view);
    renderRemoved(view);
    renderTotals(view);
    renderSegs(view);
    renderStickyBar(view);
    renderDrawer(view);
    applyPendingFocus();
  }

  /* Focus has to be applied AFTER the render that creates (or recreates) the
     element it targets — the drawer title is rewritten on open, and the swap
     button that opened the drawer is destroyed and rebuilt on close. Timing it
     with setTimeout raced the rAF render and silently dropped focus, so the
     move is queued and consumed here, at the end of the render instead. */
  function applyPendingFocus() {
    var want = S.pendingFocus;
    if (!want) return;
    S.pendingFocus = null;
    var el = null;
    if (want.kind === 'drawer') {
      var d = drawerEl();
      if (d && !d.hidden) {
        el = S.els['drawer-title'];
        if (el) el.setAttribute('tabindex', '-1');
        else { var f = focusablesIn(d); el = f[0]; }
      }
    } else if (want.kind === 'swap-trigger') {
      el = S.root.querySelector('[data-slq-swap-for="' + want.key + '"]');
    }
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  /* ------------------------------------------------------------------ */
  /* per-type theming / static hero content                              */
  /* ------------------------------------------------------------------ */

  function typeInfoMap() { return window.__STS_SKIN_TYPE_INFO__ || {}; }

  /* Do we already hold this visitor's email? Three independent sources, any of
     which means the $5 has already been claimed:
       - SLQ.state.email  — typed into the quiz in this session
       - slqSavedResults  — the guest saved their results (quiz writes this only
                            when an email was captured)
       - customerEmail    — a logged-in Shopify customer
     Wrapped in try/catch because storage throws outright in private mode. */
  function hasCapturedEmail() {
    try {
      if (window.SLQ && window.SLQ.state && window.SLQ.state.email) return true;
      if (localStorage.getItem('slqSavedResults')) return true;
      if (localStorage.getItem('customerEmail')) return true;
    } catch (e) { /* storage blocked — fall through, show the offer */ }
    return false;
  }

  function applyTheme() {
    var code = (S.skinType || '').toUpperCase();
    /* 1. the prototype palette (hand-picked deep/tint), 2. the metafield's
       single `color` with deep/tint derived, 3. neutral. */
    var pal = TYPE_PALETTE[code] || null;
    var info = typeInfoMap()[code] || null;
    var hex = info && isHex(info.color) ? info.color : null;
    var vars = pal
      ? { type: pal.hex, deep: pal.deep, tint: pal.tint, bright: brighten(pal.hex) }
      : hex
        ? { type: hex, deep: deepen(hex), tint: tintOf(hex), bright: brighten(hex) }
        : { type: NEUTRAL.hex, deep: NEUTRAL.deep, tint: NEUTRAL.tint, bright: brighten(NEUTRAL.hex) };
    S.root.style.setProperty('--slq-type', vars.type);
    S.root.style.setProperty('--slq-type-deep', vars.deep);
    S.root.style.setProperty('--slq-type-tint', vars.tint);
    S.root.style.setProperty('--slq-type-bright', vars.bright);
    // aliases used by the stylesheet (assets/sts-liquid-quiz.css) and the
    // section shell — same four values, long-form names
    S.root.style.setProperty('--slq-type-color', vars.type);
    S.root.style.setProperty('--slq-type-color-deep', vars.deep);
    S.root.style.setProperty('--slq-type-color-tint', vars.tint);
    S.root.style.setProperty('--slq-type-color-bright', vars.bright);
    // Accessible text shade for SMALL type (see inkOf). Decorative uses keep
    // `deep`; only 10-12.5px labels/chips read from --slq-type-ink.
    // 4.6 not 4.5: inkOf stops at the first 2% step that clears the target, so
    // a bare 4.5 target can land at exactly 4.50 with no margin for the
    // rounding in either the colour maths or a browser's own compositing.
    var ink = inkOf(vars.deep, vars.tint, 4.6);
    S.root.style.setProperty('--slq-type-ink', ink);
    S.root.style.setProperty('--slq-type-color-ink', ink);

    /* "Save your results · get $5" is an OFFER, so it must disappear the
       moment we already have the visitor's email — whether they typed it into
       the quiz, claimed it from the pill's own prompt, or are simply logged in.
       Leaving it up promises $5 to someone who has already been given it,
       which reads as either a broken button or a second reward that never
       arrives. */
    var saveBtn = S.els['save-results'];
    // No token = a saved copy rendered after the session ended: the save call
    // has nothing to authorise with, so the offer would only fail. Hide it.
    if (saveBtn) saveBtn.hidden = hasCapturedEmail() || !S.token;

    var num = TYPE_NUM[code] || null;
    S.els['type-code'].textContent = code || '';
    S.els['type-num'].textContent = num !== null ? String(num) : '?';
    S.els['protocol-code'].textContent = code || 'your';
    S.els['drawer-code'].textContent = code || '';

    // V2.3: inline accordion instead of the off-site learn-more button.
    // Copy comes from the v2.1 description set; no description = no accordion
    // (never guess), matching the old never-guess slug rule.
    var acc = S.els['hero-acc'];
    if (acc) {
      var quote = num !== null ? TYPE_QUOTES[num] : null;
      if (quote) {
        acc.parentNode.hidden = false;
        S.els['hero-acc-code'].textContent = code || '';
        S.els['hero-acc-desc'].textContent = quote;
      } else {
        acc.parentNode.hidden = true;
      }
    }

    renderVideo(info);
    renderTypeGrid(code);
  }

  function renderVideo(info) {
    var box = S.els['video'];
    if (!box) return; // prototype parity: results hero carries no video card
    box.innerHTML = '';
    var url = info && info.videoUrl ? String(info.videoUrl) : null;
    if (!url) { box.hidden = true; return; }
    box.hidden = false;
    var m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (m) {
      var iframe = document.createElement('iframe');
      iframe.setAttribute('src', 'https://player.vimeo.com/video/' + m[1]);
      iframe.setAttribute('title', 'About your skin type');
      iframe.setAttribute('loading', 'lazy');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      iframe.style.cssText = 'width:100%;aspect-ratio:16/9;border:0;display:block';
      box.appendChild(iframe);
    } else {
      // poster+link fallback: a real link to the real URL, nothing embedded
      var a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener');
      a.style.cssText = 'display:flex;align-items:center;gap:10px;padding:14px 18px;color:#fff;text-decoration:none;font-weight:600;font-size:14px';
      a.textContent = '▶ Watch the video about your skin type →';
      box.appendChild(a);
    }
  }

  function renderTypeGrid(myCode) {
    var sec = S.els['types-sec'];
    var grid = S.els['type-grid'];
    if (!sec || !grid) return; // V2.3: the 16-types section was removed from the page
    var infoMap = typeInfoMap();
    var codes = Object.keys(TYPE_NUM).filter(function (c) { return infoMap[c] && isHex(infoMap[c].color); });
    if (!codes.length) { sec.hidden = true; return; }
    sec.hidden = false;
    codes.sort(function (a, b) { return TYPE_NUM[a] - TYPE_NUM[b]; });
    grid.innerHTML = '';
    var setCaption = function (cap) {
      S.els['grid-cap-code'].textContent = cap;
      S.els['grid-cap-text'].textContent = cap.split('').map(function (l) { return AXES[l] || l; }).join(' · ') +
        (cap === myCode ? '  (your type)' : '');
    };
    codes.forEach(function (c) {
      var cell = clone('grid-cell'); // template root IS the wrap div
      var wrap = cell;
      var btn = tel(cell, 'btn');
      btn.setAttribute('aria-label', 'Baumann Skin Type ' + TYPE_NUM[c] + ' (' + c + ')');
      // Official octagon art (same Files icons the header MY TYPE chip uses);
      // the color fill only remains as the alt-text background on a 404.
      btn.textContent = '';
      btn.style.background = 'transparent';
      var icon = document.createElement('img');
      icon.src = 'https://cdn.shopify.com/s/files/1/0740/5984/1838/files/' + c + '.png';
      icon.alt = 'Type ' + TYPE_NUM[c];
      icon.loading = 'lazy';
      icon.style.cssText = 'display:block;width:100%;height:100%;object-fit:contain';
      icon.addEventListener('error', function () {
        icon.remove();
        btn.textContent = String(TYPE_NUM[c]);
        btn.style.background = infoMap[c].color;
      });
      btn.appendChild(icon);
      var marker = tel(cell, 'marker');
      if (c === myCode) {
        marker.hidden = false;
        marker.style.display = 'flex';
        if (TYPE_NUM[c] <= 8) { // top row — marker sits above, arrow points down
          marker.style.top = 'auto';
          marker.style.bottom = 'calc(100% + 4px)';
          marker.style.flexDirection = 'column-reverse';
          tel(cell, 'marker-arrow').innerHTML = '▼';
        }
        wrap.style.borderColor = '#1b1d3a';
      }
      btn.addEventListener('click', function () {
        S.gridCap = c;
        setCaption(c);
        var cells = grid.children;
        for (var i = 0; i < cells.length; i++) {
          cells[i].style.borderColor = (codes[i] === c || codes[i] === myCode) ? '#1b1d3a' : 'transparent';
        }
      });
      grid.appendChild(cell);
    });
    setCaption(myCode && TYPE_NUM[myCode] ? myCode : codes[0]);
  }

  /* ------------------------------------------------------------------ */
  /* sticky bar visibility (ported prototype _onScroll)                  */
  /* ------------------------------------------------------------------ */

  function updateSbVisibility() {
    if (!S || S.status !== 'ready') return;
    var el = S.root.querySelector('[data-slq-protocol-sec]');
    var show = window.innerWidth <= 760
      ? true
      : (!!el && el.getBoundingClientRect().top < window.innerHeight * 0.65);
    if (show !== S.sbVisible) {
      S.sbVisible = show;
      var view = buildView();
      renderStickyBar(view);
    }
  }

  /* ------------------------------------------------------------------ */
  /* toasts                                                              */
  /* ------------------------------------------------------------------ */


  /* ------------------------------------------------------------- save results

     The button is rendered only when hasCapturedEmail() is false, so by
     construction there is no address on file when it is clicked. It used to
     toast "your results are saved" regardless, which was untrue every time.

     The capture UI is a modal deliberately cloned from the quiz's email gate
     (sections/sts-liquid-quiz.liquid, data-slq-gate) — same backdrop, card,
     icon, type ramp — so it reads as part of the quiz, not a bolt-on.
     Submit posts to /widget/save-routine, which emails the routine and fills
     a BLANK email on the customer record (it refuses to overwrite one). */

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function saveVariantIds() {
    // Same selection the cart would receive, capped to the endpoint's limit.
    var ids = [];
    buildCartItems(buildView()).forEach(function (i) {
      var id = String(i.id || '');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.slice(0, 30);
  }

  function ensureGateAnim() {
    // The gateIn keyframe lives in the quiz section's <style>; the standalone
    // results page does not include that section, so carry the keyframe here.
    if (document.getElementById('slq-save-anim')) return;
    var st = document.createElement('style');
    st.id = 'slq-save-anim';
    st.textContent = '@keyframes slq-gateIn{from{opacity:0;transform:translate(-50%,-46%)}to{opacity:1;transform:translate(-50%,-50%)}}';
    document.head.appendChild(st);
  }

  function closeSaveModal() {
    var m = document.querySelector('[data-slq-save-modal]');
    if (m && m.parentNode) m.parentNode.removeChild(m);
    document.removeEventListener('keydown', onSaveModalKeydown, true);
    var btn = S && S.els['save-results'];
    if (btn && !btn.hidden) { try { btn.focus(); } catch (e) { /* noop */ } }
  }

  function onSaveModalKeydown(ev) {
    if (ev.key === 'Escape') { ev.stopPropagation(); closeSaveModal(); }
  }

  function openSaveResults() {
    if (document.querySelector('[data-slq-save-modal]')) return;
    ensureGateAnim();

    var wrap = document.createElement('div');
    wrap.setAttribute('data-slq-save-modal', '');

    var bd = document.createElement('div');
    bd.style.cssText = 'position:fixed;inset:0;background:rgba(20,22,50,.32);z-index:10090'; // sticky bar is z=10060
    bd.addEventListener('click', closeSaveModal);

    var dlg = document.createElement('div');
    dlg.setAttribute('role', 'dialog');
    dlg.setAttribute('aria-modal', 'true');
    dlg.setAttribute('aria-labelledby', 'slq-save-title');
    dlg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);'
      + 'width:min(380px,88vw);max-height:84vh;overflow-y:auto;background:#fff;border-radius:20px;'
      + 'padding:22px 22px 18px;z-index:10091;box-shadow:0 30px 80px rgba(15,18,50,.4);text-align:center;'
      + 'animation:slq-gateIn .45s ease';
    dlg.innerHTML =
      '<div style="width:42px;height:42px;margin:0 auto;clip-path:polygon(50% 0%,67% 9%,85% 15%,91% 33%,100% 50%,91% 67%,85% 85%,67% 91%,50% 100%,33% 91%,15% 85%,9% 67%,0% 50%,9% 33%,15% 15%,33% 9%);background:var(--slq-type-color,#2F3572);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px">&#10003;</div>'
      + '<h3 id="slq-save-title" style="font-family:var(--slq-head-font,\'Poppins\',sans-serif);font-weight:700;color:#2F3572;font-size:21px;margin-top:10px">Save your results</h3>'
      + '<p style="font-size:13px;color:#6E7076;margin-top:6px;line-height:1.45">We&rsquo;ll email your skin type and routine so you never lose them, and <b style="color:#2F3572">$10 off your first order</b> is applied at checkout.</p>'
      + '<form data-slq-save-form novalidate>'
      +   '<input type="email" data-slq-save-email placeholder="Email" autocomplete="email" style="width:100%;margin-top:14px;border:1.5px solid #DBDCDD;border-radius:11px;padding:12px 14px;font-size:14.5px;font-family:inherit;color:#26284a">'
      +   '<div data-slq-save-error role="alert" hidden style="margin-top:8px;font-size:13px;color:#c0392b;font-weight:600"></div>'
      +   '<button type="submit" data-slq-save-submit style="width:100%;margin-top:12px;background:#2F3572;color:#fff;font-weight:600;font-size:14px;padding:13px 10px;border-radius:999px;border:none;cursor:pointer;font-family:inherit;line-height:1.25">Email my results</button>'
      +   '<button type="button" data-slq-save-cancel style="width:100%;margin-top:8px;background:none;border:none;color:#6E7076;font-weight:600;font-size:13px;padding:9px;cursor:pointer;font-family:inherit;text-decoration:underline">Not now</button>'
      + '</form>';

    wrap.appendChild(bd);
    wrap.appendChild(dlg);
    // Inside the results root, NOT body: the results view sweeps unrecognised
    // body children into data-slq-chrome-hidden/display:none to keep theme
    // popups off the overlay — a body-mounted modal gets swept with them.
    // The drawer survives for exactly this reason; do what it does.
    (S.root || document.body).appendChild(wrap);
    document.addEventListener('keydown', onSaveModalKeydown, true);

    var input = dlg.querySelector('[data-slq-save-email]');
    var err = dlg.querySelector('[data-slq-save-error]');
    var submit = dlg.querySelector('[data-slq-save-submit]');
    dlg.querySelector('[data-slq-save-cancel]').addEventListener('click', closeSaveModal);
    try { input.focus(); } catch (e) { /* noop */ }

    function fail(msg) {
      err.hidden = false;
      err.textContent = msg;
      submit.disabled = false;
      submit.textContent = 'Email my results';
    }

    dlg.querySelector('[data-slq-save-form]').addEventListener('submit', function (ev) {
      ev.preventDefault();
      var email = String(input.value || '').trim().toLowerCase();
      if (!EMAIL_RE.test(email)) { fail('That does not look like an email address.'); return; }
      var ids = saveVariantIds();
      if (!ids.length) { fail('Your routine is still loading — try again in a moment.'); return; }

      submit.disabled = true;
      submit.textContent = 'Sending…';
      err.hidden = true;

      fetch(String(S.ec2Base || '').replace(/\/+$/, '') + '/widget/save-routine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + S.token },
        body: JSON.stringify({ variantIds: ids, email: email })
      })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, status: r.status, body: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body && res.body.error ? res.body.error : 'HTTP ' + res.status);
          try { localStorage.setItem('customerEmail', email); } catch (e) { /* noop */ }
          closeSaveModal();
          var btn = S.els['save-results'];
          if (btn) btn.hidden = true; // the offer is fulfilled — hide it
          toast('Sent — your routine is on its way to ' + email + '.');
        })
        .catch(function (e2) {
          // Never claim success we did not observe.
          fail(/EMAIL_IN_USE/.test(String(e2 && e2.message))
            ? 'That address is already saved to another set of results. Check your inbox for those.'
            : 'We could not send it just now. Please try again.');
          try { console.warn('[SLQ] save-routine failed:', e2 && e2.message); } catch (e3) { /* noop */ }
        });
    });
  }

  function toast(msg, ms) {
    var wrap = S && S.els['toasts'];
    if (!wrap) return;
    var node = clone('toast');
    tel(node, 'msg').textContent = msg;
    wrap.appendChild(node);
    setTimeout(function () { if (node.parentNode) node.parentNode.removeChild(node); }, ms || 3400);
  }

  /* ------------------------------------------------------------------ */
  /* add full routine to cart (spec §6 SHOP-8)                           */
  /* ------------------------------------------------------------------ */

  function cartRoute() {
    return (window.theme && window.theme.routes && window.theme.routes.cart)
      ? window.theme.routes.cart
      : '/cart';
  }

  /* This store sells overseas on Markets, so a hardcoded '/checkout' would drop
     a non-US buyer out of their localized context. Shopify.routes.root carries
     the market/locale prefix ('/' or e.g. '/en-ca/'), so build from it. */
  function checkoutRoute() {
    if (window.theme && window.theme.routes && window.theme.routes.checkout) {
      return window.theme.routes.checkout;
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/+$/, '') + '/checkout';
  }

  /* Only SUBSCRIBING routines get a code applied at add-to-cart.
   *
   * The legacy quiz (assets/quiz-results.js:233-237) also auto-applied
   * ROUTINE25 to any one-time add of 3+ items. Removed 2026-08-08 on
   * Luis's instruction: a one-time routine should NOT silently pick up a 25%
   * discount, which also made the page's "Routine total" read 17.6% higher
   * than the cart actually charged. ROUTINE25 still EXISTS in Shopify and is
   * untouched — customers can still enter it by hand; we simply stop applying
   * it for them. (IDEALROUTINE dropped 2026-08-06 — Ideal Image is no longer a
   * partner; the DRPWDERM exclusion existed only to suppress the routine
   * discount, so it goes with it.)
   *
   * Subscriptions keep the $10 first-order code — once per customer, so it
   * never repeats on renewals; the recurring 15% lives in the Recharge plans.
   * GET /discount/<code> sets Shopify's cart discount cookie; failures never
   * block the cart flow.
   *
   * The `subscribing` check below is a UX convenience, NOT the security gate.
   * Shopify CDN minification strips these comments from the served asset, but
   * string literals survive — so 'SUBSCRIBE10' is readable by anyone who opens
   * the bundle, and any /discount/ call is visible in DevTools regardless. The
   * real gate is the discount's own Purchase type = Subscription, which
   * Shopify enforces server-side on every cart.
   *
   * That gate was WRONG until 2026-08-10: SUBSCRIBE10 was configured Purchase
   * type = "One-time purchase" (contradicting its own title), so applying the
   * code took $10 off ANY cart with no subscription at all — verified live at
   * $182.00 -> $172.00 on a single one-time product. Fixed by switching it to
   * Subscription; the same attack now returns $182.00 -> $182.00. It had 0 uses
   * at the time, so nothing was lost. If you ever add another code here, set
   * its eligibility in Shopify FIRST and assume the literal is public.
   *
   * 2026-08-18 (Luis): SUBSCRIBE10 is now Purchase type = Both, minimum 3
   * items, one use per customer — "$10 off your first order" for a routine,
   * subscription or not. So it goes on EVERY routine cart; Shopify's own
   * rules (3+ items, once per customer) decide whether it applies. */
  function applyRoutineDiscount(items, rewardCode) {
    try {
      items = items || [];
      var subscribing = items.some(function (i) { return i.selling_plan; });
      var codes = [];
      if (items.length) codes.push('SUBSCRIBE10');
      if (rewardCode) codes.push(rewardCode);
      if (!codes.length) return Promise.resolve();
      /* Cart API, not the /discount/ link. Verified on this store 2026-08-18:
         /discount/A,B stores the literal "A,B" as ONE invalid code (checkout
         then drops it — and SUBSCRIBE10 with it), and a second /discount/
         call replaces the first. /cart/update.js takes a comma-separated
         `discount` string, puts every code on the cart the checkout is built
         from, and answers with per-code applicability we can act on. */
      var single = function () { return fetch('/discount/' + encodeURIComponent(codes[0])).catch(function () { /* non-blocking */ }); };
      /* stq_session rides checkout as a cart attribute -> Shopify order
         note_attributes -> webhooks/shopify.ts upsertQuizWidgetOrderAttribution
         -> quiz_widget_orders. The whole revenue-attribution pipeline existed
         and sat at 0 rows because nothing ever set this attribute (2026-08-20).
         Same session uuid the telemetry uses, so orders join the funnel. */
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: codes.join(','),
          attributes: { stq_session: trackSessionUuid() }
        })
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          var list = cart && cart.discount_codes;
          if (!list) return single(); /* Cart API refused: keep at least SUBSCRIBE10 the old way */
          var applied = list.filter(function (d) { return d && d.applicable; }).map(function (d) { return String(d.code); });
          S.appliedDiscountCodes = applied;
          if (rewardCode) {
            /* "applied" means the cart took it, not that we asked. A voucher
               the cart refuses (wrong customer, expired, one-time-only) is
               worth knowing about — without the code itself. */
            track(applied.indexOf(rewardCode) !== -1 ? 'quiz_reward_applied' : 'quiz_reward_refused',
              { value: S.quizRewardValue, reused: !!S.quizRewardReused, subscribing: subscribing });
          }
        })
        .catch(function () { return single(); });
    } catch (e) {
      return Promise.resolve();
    }
  }

  /* Skin Type Cash, applied for you (2026-08-18). The quiz's LoyaltyLion points
     (500 = the $5 voucher) used to need: log in, open the rewards widget,
     claim, paste the code. Now "Start my routine" asks the API to claim the
     taker's quiz-sized voucher and the code goes on the cart with SUBSCRIBE10.
       - fresh taker: widget token -> POST {ec2}/widget/loyalty/auto-claim
       - logged-in shopper rendered from the account: App Proxy
         POST /apps/quiz/loyalty/auto-claim (Shopify signs who they are)
       - guest without an email: nothing to claim, no call
     Resolves to the code or null; capped at 5 s and never rejects — the
     checkout must not wait on, or break because of, the loyalty API. */
  function claimQuizReward(attempt) {
    attempt = attempt || 1;
    try {
      var base = String(S.ec2Base || '').replace(/\/+$/, '');
      var root = document.querySelector('[data-slq-root]');
      var loggedIn = !!(root && root.getAttribute('data-logged-in') === 'true');
      var req;
      if (S.token && base) {
        req = fetch(base + '/widget/loyalty/auto-claim', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + S.token },
          body: '{}'
        });
      } else if (loggedIn) {
        var proxyBase = String((root && root.getAttribute('data-proxy-base')) || '/apps/quiz').replace(/\/+$/, '');
        req = fetch(proxyBase + '/loyalty/auto-claim?nonce=' + encodeURIComponent(slqUuid()), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' }, body: '{}'
        });
      } else {
        return Promise.resolve(null);
      }
      var timeout = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, 5000); });
      return Promise.race([
        req.then(function (r) { return (r.ok && r.status !== 204) ? r.json() : null; }).catch(function () { return null; }),
        timeout
      ]).then(function (j) {
        /* A brand-new taker's points land a few seconds after the customer is
           created (Flow -> LoyaltyLion), and LoyaltyLion may not even have the
           customer yet. If they click fast, wait 2 s and try ONCE more. */
        if (j && !j.ok && attempt === 1 && (j.reason === 'NOT_ENROLLED' || j.reason === 'NOT_ENOUGH_POINTS')) {
          return new Promise(function (resolve) { setTimeout(resolve, 2000); }).then(function () { return claimQuizReward(2); });
        }
        S.quizRewardLastReason = (j && !j.ok && j.reason) ? String(j.reason) : null;
        if (!j || !j.ok || !j.code || !/^[A-Za-z0-9_-]{4,64}$/.test(String(j.code))) return null;
        S.quizRewardCode = String(j.code);
        S.quizRewardValue = (typeof j.value === 'number') ? j.value : null;
        S.quizRewardReused = !!j.reused;
        track('quiz_reward_claimed', { value: S.quizRewardValue, reused: S.quizRewardReused });
        return S.quizRewardCode;
      }).catch(function () { return null; });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  /* Results page: claim the quiz voucher as soon as the routine renders and
     say so (Luis, 2026-08-18): "Congrats, $5 has been applied to checkout for
     taking the quiz for the first time." A brand-new taker's points land a few
     seconds after Flow -> LoyaltyLion, so this retries quietly for ~40 s and
     then gives up without a word; "Start my routine" claims again anyway (the
     API hands back the same voucher, reused:true) so nothing is lost either
     way. Runs once per page. */
  function eagerQuizRewardNotice() {
    if (!S || S.rewardNoticeStarted) return;
    S.rewardNoticeStarted = true;
    var root = document.querySelector('[data-slq-root]');
    var loggedIn = !!(root && root.getAttribute('data-logged-in') === 'true');
    /* Luis (2026-08-18): the email is the $10 (SUBSCRIBE10, applied to the
       routine cart at checkout), the account is the $5 in Skin Type Cash. So a
       taker who gave an email hears about the $10 right away, and the $5
       toast is for account holders (the gate's password lands them here
       logged in). An email-only taker still gets the $5 voucher applied at
       checkout — quietly. */
    if (S.token && hasCapturedEmail()) {
      toast('Congrats, $10 off your first order has been applied to checkout for entering your email.', 8000);
    }
    if (!loggedIn) return;
    // Flow -> LoyaltyLion took 1-3 min for today's test takers, so keep
    // asking (cheaply) for ~5 min before going quiet.
    var delays = [1500, 5000, 12000, 25000, 45000, 75000, 120000];
    var attempt = 0;
    var tryOnce = function () {
      claimQuizReward().then(function (code) {
        if (!S) return;
        if (code) {
          var v = (typeof S.quizRewardValue === 'number') ? S.quizRewardValue : 5;
          toast(S.quizRewardReused
            ? 'Your $' + v + ' Skin Type Cash is applied at checkout.'
            : 'Congrats, $' + v + ' Skin Type Cash has been applied to checkout for creating your account.', 8000);
          return;
        }
        var again = /NOT_ENROLLED|NOT_ENOUGH_POINTS/.test(String(S.quizRewardLastReason || ''));
        if (again && ++attempt < delays.length) setTimeout(tryOnce, delays[attempt]);
      }).catch(function () { /* quiet */ });
    };
    setTimeout(tryOnce, delays[0]);
  }

  function buildCartItems(view) {
    var items = [];
    var seen = new Set();
    unitSteps(view).forEach(function (u) {
      if (!isBundleAddableStep(u)) return;
      var id = Number(u.dtcVariantId);
      if (!isFinite(id) || id <= 0) return;
      var sub = subFor(u);
      var key = id + '::' + (sub ? sub.planId : ''); // dedupe key is (variantId, selling_plan)
      if (seen.has(key)) return;
      seen.add(key);
      var item = { id: id, quantity: 1 };
      if (sub) item.selling_plan = sub.planId;
      items.push(item);
    });
    return items;
  }

  function setAddingState(adding) {
    S.adding = adding;
    ['hero-checkout', 'sb-checkout'].forEach(function (name) {
      var btn = S.els[name];
      if (!btn) return;
      btn.disabled = adding;
      btn.style.opacity = adding ? '.6' : '';
      btn.innerHTML = adding ? 'Adding…' : 'Start my routine →';
    });
  }

  function showCartError(message) {
    var box = S.els['cart-error'];
    if (box) {
      box.hidden = false;
      box.textContent = message;
    }
    toast('We couldn’t add your routine to the cart. ' + message);
  }

  function clearCartError() {
    var box = S.els['cart-error'];
    if (box) { box.hidden = true; box.textContent = ''; }
  }

  /* The free "Skin Type Cash Reward" line is added by the THEME's cashback
     logic (layout/theme.liquid), which listens for cart changes and runs on a
     throttled setTimeout(..., 500). Going straight to checkout beats that
     timer, so the reward silently never made it onto the order — measured as
     4 line items instead of 5 on 2026-08-10.

     Call the theme's own updaters directly and wait for them, exactly as the
     legacy engine did (assets/quiz-results.js:242-249). Sequential, because
     updateCartItemGCValue() reads the cart that updateCartWithGCValue() just
     rewrote. Failures are swallowed: a missing reward line must never block
     someone from checking out. */
  function applyCashbackLines() {
    return Promise.resolve()
      .then(function () {
        return typeof window.updateCartWithGCValue === 'function' ? window.updateCartWithGCValue() : null;
      })
      .then(function () {
        return typeof window.updateCartItemGCValue === 'function' ? window.updateCartItemGCValue() : null;
      })
      .then(waitForCartSettle)
      .catch(function () { /* never block checkout on the reward line */ });
  }

  /* theme.liquid only writes the `gc-value` cart ATTRIBUTE — it never adds the
     free "Skin Type Cash Reward" line (grep: no /cart/add in layout/theme.liquid).
     That line is added asynchronously by the cart app, so rather than couple to
     a third party's internals, just wait for the cart to stop changing.

     Poll until item_count is identical on two consecutive reads, capped at
     CART_SETTLE_MAX_MS so a misbehaving app can never strand someone on a
     spinner. Typical cost is one extra poll (~300ms) with the button still
     reading "Adding…". */
  var CART_SETTLE_INTERVAL_MS = 300;
  var CART_SETTLE_MAX_MS = 2000;

  function waitForCartSettle() {
    var started = Date.now();
    var lastCount = null;
    function poll() {
      return fetch(cartRoute() + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = cart && cart.item_count;
          if (lastCount !== null && count === lastCount) return null;   // settled
          if (Date.now() - started >= CART_SETTLE_MAX_MS) return null;  // capped
          lastCount = count;
          return new Promise(function (resolve) { setTimeout(resolve, CART_SETTLE_INTERVAL_MS); }).then(poll);
        })
        .catch(function () { return null; }); // a failed poll must not block checkout
    }
    return poll();
  }

  function addRoutineToCart() {
    if (S.adding) return;
    clearCartError();
    var view = buildView();
    var items = buildCartItems(view);
    var variantIds = items.map(function (i) { return String(i.id); });
    if (!items.length) {
      showCartError('No purchasable items are currently in your routine.');
      track('add_to_cart', { variantIds: [], ok: false, failedIds: [], reason: 'no_addable_items' });
      return;
    }
    setAddingState(true);
    /* Skip anything the visitor already has in the cart. /cart/add.js does NOT
       replace a matching line, it INCREMENTS its quantity — so a shopper who
       browsed a product in, or who taps "Start my routine" on both the hero and
       the sticky bar, ended up buying two of it. Dedupe is by VARIANT alone,
       deliberately ignoring the selling plan: two lines of the same cream, one
       one-time and one subscription, is still the same product twice to the
       person paying. */
    fetch(cartRoute() + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; })   // cart unreadable: fall back to adding everything
      .then(function (cart) {
        var already = {};
        (cart && cart.items || []).forEach(function (li) {
          if (li && li.id !== null && li.id !== undefined) already[String(li.id)] = true;
        });
        var toAdd = items.filter(function (i) { return !already[String(i.id)]; });
        if (!toAdd.length) return null;   // whole routine is already in the cart
        return fetch(cartRoute() + '/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: toAdd })
        }).then(function (r) {
          if (!r.ok) {
            return r.json().then(
              function (body) { throw new Error((body && (body.description || body.message)) || 'Cart add failed (' + r.status + ')'); },
              function () { throw new Error('Cart add failed (' + r.status + ')'); }
            );
          }
          return r.json();
        });
      })
      /* Pass the FULL routine, not just what was added: when every product was
         already in the cart nothing is added, but the cart is still a
         subscription cart and must still earn SUBSCRIBE10. The Skin Type Cash
         voucher (if the shopper has one to claim) goes on in the same call. */
      .then(function () { return claimQuizReward(); })
      .then(function (rewardCode) { return applyRoutineDiscount(items, rewardCode); })
      .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        track('add_to_cart', { variantIds: variantIds, ok: true });
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart } }));
        return applyCashbackLines();
      })
      .then(function () {
        // Both "Start my routine" buttons (hero + sticky bar) go STRAIGHT to
        // checkout — the routine has already been curated and priced on this
        // page, so the cart page and the drawer are both just a speed bump
        // between the visitor and paying.
        //
        // Safe to navigate here: this runs after applyRoutineDiscount() and
        // applyCashbackLines() in the same promise chain, so the subscription
        // code and the cashback line are both already on the cart.
        //
        // setAddingState(false) is deliberately NOT called on this path — the
        // button stays "Adding…" until the browser leaves, which also stops a
        // double-click from firing a second add. The .catch below re-enables it
        // if we never get here.
        track('begin_checkout', { itemCount: variantIds.length });
        window.location.href = checkoutRoute();
      })
      .catch(function (err) {
        setAddingState(false);
        var reason = String((err && err.message) || err);
        track('add_to_cart', { variantIds: variantIds, ok: false, failedIds: variantIds, reason: reason });
        showCartError(reason);
      });
  }

  /* ------------------------------------------------------------------ */
  /* regimen fetch + results_viewed                                      */
  /* ------------------------------------------------------------------ */

  function fireResultsViewed(steps) {
    if (S.viewedFired) return;
    S.viewedFired = true;
    if (!steps) {
      // degraded settle — visitor genuinely viewed a product-less results screen
      track('results_viewed', { productCount: 0, totalShown: 0, inStockCount: 0, substitutedCount: 0 });
      return;
    }
    var view = buildView();
    var products = unitSteps(view).filter(function (s) { return !isInformationalStep(s); });
    track('results_viewed', {
      productCount: products.length,
      totalShown: computeTotals(view).now,
      inStockCount: products.filter(function (s) { return s.inStock !== false; }).length,
      substitutedCount: products.filter(function (s) { return s.substituted === true; }).length,
      // true when the server rebuilt this routine from the skin-type template
      // because the regimen had no steps — the visitor saw products, but the
      // underlying write failure still needs counting.
      fallback: S.fallbackPayload === true
    });
  }

  function normalizeStep(raw) {
    var step = Object.assign({}, raw);
    if (!Array.isArray(step.keyIngredients)) step.keyIngredients = [];
    if (!Array.isArray(step.swapCandidates)) step.swapCandidates = [];
    step.swapCandidates = step.swapCandidates.map(function (c) {
      var cc = Object.assign({}, c);
      if (!Array.isArray(cc.keyIngredients)) cc.keyIngredients = [];
      return cc;
    });
    // Runs after candidates are normalised, so the replacement it picks has the
    // same shape as any other candidate.
    return substituteOutOfStock(step);
  }

  /* ---------------------------------------------------------------- */
  /* loading sequence — mirrors the quiz's "Calculating" screen         */
  /* ---------------------------------------------------------------- */
  /* Advances the checklist while /widget/regimen is in flight so the hand-off
   * from quiz to results reads as one continuous moment.
   *
   * It deliberately STOPS ONE ROW SHORT. Only real loaded data may complete the
   * last row (finish(), called on 'ready'). A slow or hanging fetch therefore
   * keeps spinning rather than presenting a finished checklist over a page that
   * has nothing on it — the same "never fabricate progress" rule the rest of
   * this file follows for totals and stock. */
  var slqLoadingSeq = (function () {
    var timer = null, idx = 0, rows = [], bar = null;

    function paint() {
      for (var i = 0; i < rows.length; i++) {
        var state = i < idx ? 'done' : (i === idx ? 'active' : 'pending');
        rows[i].setAttribute('data-state', state);
        var d = rows[i].querySelector('[data-slq-load-done]');
        var a = rows[i].querySelector('[data-slq-load-active]');
        var p = rows[i].querySelector('[data-slq-load-pending]');
        if (d) d.hidden = state !== 'done';
        if (a) a.hidden = state !== 'active';
        if (p) p.hidden = state !== 'pending';
      }
      if (bar) bar.style.width = Math.round((idx / rows.length) * 100) + '%';
    }

    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    return {
      start: function (root) {
        var host = root && root.querySelector('[data-slq-loading]');
        if (!host) return;
        rows = [].slice.call(host.querySelectorAll('[data-slq-load-row]'));
        bar = host.querySelector('[data-slq-load-bar]');
        if (!rows.length) return;
        stop();
        idx = 0;
        paint();
        timer = setInterval(function () {
          if (idx >= rows.length - 1) { stop(); return; }  // hold on the last row
          idx++;
          paint();
        }, 900);
      },
      finish: function () { stop(); if (rows.length) { idx = rows.length; paint(); } },
      reset: function () { stop(); idx = 0; if (rows.length) paint(); }
    };
  })();

  /* The error card's actions depend on WHY the routine did not load
     (Luis, 2026-08-18: "Try again" is useless when the session is simply
     gone). 'expired'/'missing' = no usable session: offer Retake the quiz,
     and Log in to see your results for a logged-out shopper (an account
     renders straight from Shopify's signed identity, no session needed);
     'transient' = the fetch itself failed: keep Try again, offer Retake too. */
  function showErrorActions(kind) {
    var root = document.querySelector('[data-slq-root]');
    var quizUrl = (root && root.getAttribute('data-quiz-url')) || '/pages/skin-type-quiz';
    var loggedIn = !!(root && root.getAttribute('data-logged-in') === 'true');
    var retry = S && S.els['error-retry'], retake = S && S.els['error-retake'], login = S && S.els['error-login'];
    if (retry) retry.hidden = kind !== 'transient';
    if (retake) { retake.href = quizUrl; retake.hidden = false; }
    if (login) {
      login.hidden = loggedIn || kind === 'transient';
      login.href = '/account/login?return_url=' + encodeURIComponent(window.location.pathname + window.location.search);
    }
  }

  function showState(which) {
    S.root.querySelector('[data-slq-loading]').hidden = which !== 'loading';
    S.root.querySelector('[data-slq-error]').hidden = which !== 'error';
    S.root.querySelector('[data-slq-body]').hidden = which !== 'ready';
    if (which === 'loading') slqLoadingSeq.start(S.root);
    else if (which === 'ready') slqLoadingSeq.finish();
    else slqLoadingSeq.reset();
  }

  /* ---------------------------------------------------------------- */
  /* saved-copy cache (guests who gave us an email)                     */
  /* ---------------------------------------------------------------- */
  /* The widget token lives 2 h. A visitor who comes back later would otherwise
   * hit a 401 and be told to retake the quiz. So on every successful live load
   * we also cache the RENDERED payload, and fall back to it when the live fetch
   * fails or there is no token at all.
   *
   * Live data always wins: the cache is a fallback, never a shortcut. Prices
   * and stock in a cached copy are as old as `cachedAt`, so the page says so
   * when it renders from one, and anything older than the cap is discarded
   * rather than shown. EVERY taker gets the cache (Luis, 2026-08-18) — it used
   * to be written only for visitors who saved their results, which left a
   * guest who skipped the email box with "your session has expired" two hours
   * after finishing the quiz. */
  var CACHE_KEY = 'slqSavedRegimen';
  var CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  function cacheRegimen(data) {
    // Never persist a stepless payload: the cached-fallback path would then
    // replay the failure on every future visit even after the server recovered.
    if (!data || !Array.isArray(data.steps) || data.steps.length === 0) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        steps: data.steps,
        ingredientInfo: data.ingredientInfo || null,
        skinType: S.skinType || '',
        sensitivity: S.sensitivity || '',
        cachedAt: Date.now()
      }));
    } catch (e) { /* quota or blocked storage — the live path still worked */ }
  }

  function readCachedRegimen() {
    try {
      var c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!c || !Array.isArray(c.steps) || !c.steps.length) return null;
      if (!c.cachedAt || (Date.now() - c.cachedAt) > CACHE_MAX_AGE_MS) return null;
      // A cache from a DIFFERENT skin type is someone else's routine (shared
      // device) or a stale one from before a re-quiz — never render it.
      // `S` is null when the page bootstrap probes this before init().
      if (S && S.skinType && c.skinType && c.skinType !== S.skinType) return null;
      return c;
    } catch (e) { return null; }
  }

  /* The routine card wears a pulsing "Live" pill. On a cached copy that is
   * simply untrue — the prices are as old as the cache — so it comes off, and
   * goes back on for a live render. NOTE: `hidden` alone does nothing here;
   * the pill carries an inline `display:flex` that outranks it. */
  function setLivePill(visible) {
    var pills = S.root.querySelectorAll('[data-slq-live-pill]');
    for (var p = 0; p < pills.length; p++) pills[p].style.display = visible ? 'flex' : 'none';
  }

  function showCacheNotice(cachedAt) {
    var body = S.root.querySelector('[data-slq-body]');
    if (!body) return;
    setLivePill(false);
    var note = S.root.querySelector('[data-slq-cache-note]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-slq-cache-note', '');
      note.style.cssText = 'margin:0;padding:10px 18px;text-align:center;font-size:13px;color:#6E7076;background:#F1F1EE';
      body.insertBefore(note, body.firstChild);
    }
    // A saved copy has no live session behind it: the "Save your results"
    // call would only fail (no token, or an expired one), so the offer goes.
    var saveBtn = S.els['save-results'];
    if (saveBtn) saveBtn.hidden = true;
    var days = Math.floor((Date.now() - cachedAt) / (24 * 60 * 60 * 1000));
    note.textContent = 'Showing your saved routine' +
      (days > 0 ? ' from ' + days + ' day' + (days === 1 ? '' : 's') + ' ago' : '') +
      '. Prices and availability may have changed since.';
  }

  function renderPayload(data) {
    // A CACHED payload carries the skin type + sensitivity it was captured
    // with; a live one never does. Adopt them when init had none — that is the
    // "landed here cold with only a saved copy" path, and without it the page
    // would render in the default theme with no Essentials Kit.
    if (!S.skinType && data.skinType) {
      S.skinType = data.skinType;
      S.sensitivity = S.sensitivity || data.sensitivity || '';
    }
    /* Re-theme unconditionally. applyTheme() also runs at init(), which can be
       before the skin type is known, and it used to only re-run when S.skinType
       had been EMPTY — so a page that already knew its type never repainted and
       kept the neutral indigo. It is idempotent, so calling it again is free. */
    applyTheme();
    // Optional, additive: absent on older API builds → chips render without
    // tooltips, exactly as before.
    S.ingredientInfo = (data.ingredientInfo && typeof data.ingredientInfo === 'object') ? data.ingredientInfo : null;
    /* A ZERO-step payload is a failure, not a routine. The quiz flow really
       does produce these — 3 regimens in 90 days on dev arrived with quizid
       set and no steps at all (a scoring-write failure), and rendering them
       "ready" produced a QUIZ COMPLETE page reading "0 steps · $0", which the
       visitor can only read as "the quiz broke and no one told me". Surface
       the honest error state (with its retry button) instead. */
    if (!Array.isArray(data.steps) || data.steps.length === 0) {
      S.status = 'error';
      showState('error');
      if (S.els['error-msg']) {
        S.els['error-msg'].textContent =
          'We couldn’t build your routine this time. Please tap Try again — and if this keeps happening, retake the quiz.';
      }
      showErrorActions('transient');
      fireResultsViewed(null); // null = degraded settle; [] would buildView() on stale state
      return;
    }
    // Server-rebuilt routine (zero-step regimen recovered from the skin-type
    // template). Recorded into results_viewed so the failure stays countable
    // even though the visitor now sees a full routine.
    S.fallbackPayload = data.fallback === true;
    S.steps = data.steps.map(normalizeStep);
    S.status = 'ready';
    showState('ready');
    fireResultsViewed(S.steps);
    renderAll();
    updateSbVisibility();
  }

  function fetchRegimen() {
    S.status = 'loading';
    showState('loading');
    var base = String(S.ec2Base || '').replace(/\/+$/, '');
    fetch(base + '/widget/regimen', { headers: { Authorization: 'Bearer ' + S.token } })
      .then(function (r) {
        if (!r.ok) {
          var err = new Error('regimen fetch failed with status ' + r.status);
          err.status = r.status;
          throw err;
        }
        return r.json();
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.steps)) throw new Error('regimen payload missing a steps array');
        // Live data — undo anything a previous cached render left behind
        // (re-init reuses the same DOM; a retry after a 401 lands here).
        var stale = S.root.querySelector('[data-slq-cache-note]');
        if (stale) stale.remove();
        setLivePill(true);
        renderPayload(data);
        eagerQuizRewardNotice();
        // Shopify owns the prices: re-price everything on the page in the
        // buyer's market before the shopper reads a total. Cache AFTER, so the
        // saved copy holds market-correct figures too.
        fetchPresentmentPrices().then(function () { cacheRegimen(data); });
      })
      .catch(function (err) {
        var cached = readCachedRegimen();
        if (cached) {
          renderPayload(cached);
          showCacheNotice(cached.cachedAt);
          return;
        }
        S.status = 'error';
        showState('error');
        var msg, kind;
        if (err && err.status === 401) { kind = 'expired'; msg = 'Your results session has expired. Retake the quiz — or log in if you saved your results to an account.'; }
        else if (err && err.status === 404) { kind = 'missing'; msg = 'We couldn’t find a routine for this session. Retake the quiz — or log in if you saved your results to an account.'; }
        else { kind = 'transient'; msg = 'Something went wrong loading your routine (' + String((err && err.message) || err) + '). Please try again.'; }
        S.els['error-msg'].textContent = msg;
        showErrorActions(kind);
        fireResultsViewed(null);
      });
  }

  /* Render straight from the saved copy — used when there is no usable token
   * at all, so we never fire a fetch that is certain to fail. */
  function renderFromCacheOnly() {
    var cached = readCachedRegimen();
    if (!cached) return false;
    renderPayload(cached);
    showCacheNotice(cached.cachedAt);
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* static event wiring                                                 */
  /* ------------------------------------------------------------------ */

  function bindStatic() {
    var on = function (name, handler) {
      var el = S.els[name];
      if (el) el.addEventListener('click', handler);
    };
    // Tapping anywhere else dismisses an open ingredient popover (chips
    // stopPropagation on their own click, so this only fires for outside taps).
    // Bound on `document`, NOT S.root: on the standalone results page the site
    // header and footer sit outside our root, so a root-scoped listener left
    // popovers stuck open when the visitor clicked the real page chrome. (On
    // the quiz page the takeover hid that chrome, which is why it never showed
    // up there.) Bound once per page — `bindStatic` runs once per root.
    document.addEventListener('click', function () { closeAllIngPops(); });
    // Popovers are positioned fixed from the chip's rect at open time (see
    // positionIngPop) — a scroll or resize would leave one floating over the
    // wrong thing, so close them instead. Capture: inner scroll containers too.
    document.addEventListener('scroll', function () { if (!isIngSheetMode()) closeAllIngPops(); }, { passive: true, capture: true });
    window.addEventListener('resize', function () { closeAllIngPops(); });
    var segs = S.root.querySelectorAll('[data-slq-seg]');
    for (var i = 0; i < segs.length; i++) {
      (function (b) {
        b.addEventListener('click', function () { setSize(b.getAttribute('data-slq-seg')); });
      })(segs[i]);
    }
    var tods = S.root.querySelectorAll('[data-slq-tod]');
    for (var j = 0; j < tods.length; j++) {
      (function (b) {
        b.addEventListener('click', function () { setTod(b.getAttribute('data-slq-tod')); });
      })(tods[j]);
    }
    var gtabs = S.root.querySelectorAll('[data-slq-glance-tab]');
    for (var k = 0; k < gtabs.length; k++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.glanceTab = b.getAttribute('data-slq-glance-tab');
          S.tod = S.glanceTab;
          scheduleRender();
        });
      })(gtabs[k]);
    }
    var mtabs = S.root.querySelectorAll('[data-slq-mob-tab]');
    for (var m = 0; m < mtabs.length; m++) {
      (function (b) {
        b.addEventListener('click', function () { setTod(b.getAttribute('data-slq-mob-tab')); });
      })(mtabs[m]);
    }
    on('see-details', function () {
      var el = S.root.querySelector('[data-slq-protocol-sec]');
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 70, behavior: 'smooth' });
    });
    on('hero-checkout', addRoutineToCart);
    on('sb-checkout', addRoutineToCart);
    on('save-results', openSaveResults);
    on('hero-acc', function () {
      var open = S.els['hero-acc-desc'].hidden;
      S.els['hero-acc-desc'].hidden = !open;
      S.els['hero-acc'].setAttribute('aria-expanded', open ? 'true' : 'false');
      S.els['hero-acc-arrow'].innerHTML = open ? '&#9652;' : '&#9662;';
    });
    on('sb-min', function () { S.sbOpen = false; scheduleRender(); });
    on('sb-collapsed', function () { S.sbOpen = true; scheduleRender(); });
    on('sb-sub-switch', toggleMasterSub);
    on('master-sub-switch', toggleMasterSub);
    on('drawer-close', closeDrawer);
    on('drawer-overlay', closeDrawer);
    on('error-retry', fetchRegimen);

    S.onScroll = function () { updateSbVisibility(); };
    S.onResize = function () { updateSbVisibility(); scheduleRender(); reclampAllIngRows(); };
    window.addEventListener('scroll', S.onScroll, { passive: true });
    window.addEventListener('resize', S.onResize);
  }

  function unbindWindow() {
    if (S && S.onScroll) window.removeEventListener('scroll', S.onScroll);
    if (S && S.onResize) window.removeEventListener('resize', S.onResize);
  }

  /* ------------------------------------------------------------------ */
  /* public API                                                          */
  /* ------------------------------------------------------------------ */

  /* Review support: the results page talks to its own API host, separate from
     the quiz page's. Without this, anything not yet on production is
     untestable through the UI — the only way to exercise it would be to ship it
     to production first, which defeats the point of testing it.

     Allowlisted deliberately. A host is never read from the URL. */
  var ENV_EC2 = {
    dev: 'https://api-dev.skintype.pro',
    prod: 'https://api.skintype.pro'
  };

  function envEc2Override() {
    try {
      var v = new URLSearchParams(window.location.search).get('slqEnv');
      return (v && ENV_EC2[v.toLowerCase()]) || '';
    } catch (e) { return ''; }
  }

  function showEnvBanner(base) {
    try {
      if (document.querySelector('[data-slq-env-banner]')) return;
      var b = document.createElement('div');
      b.setAttribute('data-slq-env-banner', '');
      b.textContent = 'Preview: routine loaded from ' + base.replace('https://', '');
      b.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;'
        + 'background:#2F3572;color:#fff;font:600 12px/1.5 system-ui,sans-serif;'
        + 'text-align:center;padding:6px 12px;letter-spacing:.02em';
      (document.body || document.documentElement).appendChild(b);
    } catch (e) { /* banner is advisory only */ }
  }


  /* ------------------------------------------------------------- email claim

     The confirmation email for a re-quiz taken ON THIS STORE links back here
     with ?claim=<nonce>&ct=<token>. Show the parked result in the same modal
     language as the rest of the quiz; "Apply my new results" commits it
     server-side (transfer-iff-the-code-differed, like every commit path) and
     returns a widget token so this page can render the live regimen — no
     account, no portal, the shopper never leaves the store. */

  function slqClaimParams() {
    try {
      var q = new URLSearchParams(window.location.search);
      var nonce = q.get('claim'); var ct = q.get('ct');
      if (nonce && ct && /^[a-f0-9]{24,64}$/i.test(nonce) && /^[a-f0-9]{24,64}$/i.test(ct)) {
        return { nonce: nonce, ct: ct };
      }
    } catch (e) { /* noop */ }
    return null;
  }

  function slqClaimModal(inner) {
    ensureGateAnim();
    var wrap = document.createElement('div');
    wrap.setAttribute('data-slq-claim-modal', '');
    // The results page's fullscreen takeover hides late-added BODY children;
    // when this mounts on body (engine not initialised — empty/other state)
    // it must survive that sweep, so opt out the way the drawer does.
    wrap.setAttribute('data-slq-keep', '');
    var bd = document.createElement('div');
    bd.style.cssText = 'position:fixed;inset:0;background:rgba(20,22,50,.32);z-index:10090';
    var dlg = document.createElement('div');
    dlg.setAttribute('role', 'dialog');
    dlg.setAttribute('aria-modal', 'true');
    dlg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);'
      + 'width:min(380px,88vw);max-height:84vh;overflow-y:auto;background:#fff;border-radius:20px;'
      + 'padding:22px 22px 18px;z-index:10091;box-shadow:0 30px 80px rgba(15,18,50,.4);text-align:center;'
      + 'animation:slq-gateIn .45s ease';
    dlg.innerHTML = inner;
    wrap.appendChild(bd); wrap.appendChild(dlg);
    // Inside the results root when the engine is up (the results view sweeps
    // unrecognised body children); before init there is no sweep, so body.
    ((S && S.root) || document.body).appendChild(wrap);
    return { wrap: wrap, dlg: dlg, close: function () { if (wrap.parentNode) wrap.parentNode.removeChild(wrap); } };
  }

  // The pending claim the delegated click handler acts on. Set when the modal
  // opens; cleared when it closes. Node-attached listeners were found stripped
  // on the live page (markup intact, handlers gone, no error), so the buttons
  // are handled by ONE capture-phase listener on document instead — delegation
  // survives whatever replaces the nodes.
  var pendingClaim = null;

  /* Shared markup for the "you have new results" prompt. `mode`:
       'token' — emailed link (?claim=&ct=), POSTs the token to EC2 directly;
       'login' — the customer is logged in to the store; POSTs through the App
                 Proxy, whose Shopify-signed logged_in_customer_id is the proof. */
  function claimPromptHtml(type, mode) {
    return '<div style="width:42px;height:42px;margin:0 auto;clip-path:polygon(50% 0%,67% 9%,85% 15%,91% 33%,100% 50%,91% 67%,85% 85%,67% 91%,50% 100%,33% 91%,15% 85%,9% 67%,0% 50%,9% 33%,15% 15%,33% 9%);background:var(--slq-type-color,#2F3572);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px">&#10003;</div>'
      + '<h3 style="font-family:var(--slq-head-font,\'Poppins\',sans-serif);font-weight:700;color:#2F3572;font-size:21px;margin-top:10px">You have new skin type results</h3>'
      + '<p style="font-size:13px;color:#6E7076;margin-top:6px;line-height:1.45">Your new result' + (type ? ' (<b style="color:#2F3572">' + type + '</b>)' : '') + ' from your recent quiz is ready. Nothing changes until you apply it' + (mode === 'login' ? ' — it will replace the results saved on your account' : '') + '.</p>'
      + '<div data-slq-claim-error role="alert" hidden style="margin-top:8px;font-size:13px;color:#c0392b;font-weight:600"></div>'
      + '<button type="button" data-slq-claim-apply style="width:100%;margin-top:12px;background:#2F3572;color:#fff;font-weight:600;font-size:14px;padding:13px 10px;border-radius:999px;border:none;cursor:pointer;font-family:inherit;line-height:1.25">Apply my new results</button>'
      + '<button type="button" data-slq-claim-cancel style="width:100%;margin-top:8px;background:none;border:none;color:#6E7076;font-weight:600;font-size:13px;padding:9px;cursor:pointer;font-family:inherit;text-decoration:underline">' + (mode === 'login' ? 'Keep my current results' : 'Not now') + '</button>';
  }

  function closeClaimModal() {
    var m = document.querySelector('[data-slq-claim-modal]');
    if (m && m.parentNode) m.parentNode.removeChild(m);
    pendingClaim = null;
  }

  document.addEventListener('click', function (ev) {
    var el = ev.target;
    while (el && el !== document) {
      if (el.hasAttribute && el.hasAttribute('data-slq-claim-cancel')) { closeClaimModal(); return; }
      if (el.hasAttribute && el.hasAttribute('data-slq-claim-logout')) {
        /* Wrong account: remember this page so the NEXT login (as the right
           account) lands straight back on the claim prompt — quiz.liquid
           (legacy, still in the layout) redirects a freshly logged-in customer
           to localStorage.checkout_url. Then log this session out. */
        try { localStorage.setItem('checkout_url', el.getAttribute('data-slq-claim-logout') || (window.location.pathname + window.location.search)); } catch (e0) { /* noop */ }
        return; // the anchor's own href (/account/logout) does the rest
      }
      if (el.hasAttribute && el.hasAttribute('data-slq-claim-apply')) break;
      el = el.parentNode;
    }
    if (!el || el === document || !pendingClaim) return;

    var apply = el;
    var p = pendingClaim;
    apply.disabled = true;
    apply.textContent = 'Applying…';
    var req = p.mode === 'login'
      // App Proxy call: same-origin, Shopify signs logged_in_customer_id on the
      // way through — that signature is the authorisation, not anything here.
      ? fetch(String(p.proxyBase || '/apps/quiz').replace(/\/+$/, '') + '/staged-results/' + p.nonce + '/apply?nonce=' + encodeURIComponent(slqUuid()), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' }, body: '{}',
        })
      : fetch(p.base + '/quiz/staged-results/' + p.nonce + '/claim', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ claimToken: p.ct }),
        });
    req
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        if (!res.ok || !res.body || !res.body.ok) throw new Error((res.body && res.body.error) || 'FAILED');
        var onApplied = p.onApplied;
        closeClaimModal();
        // The store's header badge, product-page type matching and the quiz's
        // own prefill read these two allow-listed keys (the quiz writes them on
        // a direct success). Applying a parked result IS that success — keep
        // them in step so the store reflects the applied type immediately, not
        // on the next logged-in page load.
        try {
          if (res.body.skinType) localStorage.setItem('customerSkinType', String(res.body.skinType));
          if (res.body.sensitivity) localStorage.setItem('customerSensitivityList', JSON.stringify([String(res.body.sensitivity)]));
        } catch (e) { /* storage blocked */ }
        if (res.body.widgetToken) {
          if (S && S.ec2Base) {
            // The engine already knows a type (the one this page rendered) and
            // renderPayload() only adopts a type when it has NONE, so without
            // this the routine below re-fetched as the NEW type while the hero
            // ("Your Baumann Skin Type Is DSPT"), the palette, the type grid
            // and the saved copy all stayed on the OLD one. Hand it the applied
            // type + sensitivity first; applyTheme() repaints from S.skinType.
            if (res.body.skinType) S.skinType = String(res.body.skinType).toUpperCase();
            if (res.body.sensitivity) S.sensitivity = String(res.body.sensitivity).toUpperCase();
            S.token = res.body.widgetToken;
            fetchRegimen();
          } else if (typeof onApplied === 'function') {
            // Engine not initialised (page was in its empty/other state) —
            // hand the fresh token to the page bootstrap to render.
            onApplied({ widgetToken: res.body.widgetToken, skinType: res.body.skinType || '' });
          }
        }
        if (S && S.ec2Base) {
          toast(res.body.transferred && res.body.practiceName
            ? 'New results saved — you’re now with ' + res.body.practiceName + '.'
            : 'New results saved.');
        }
      })
      .catch(function (err) {
        var m2 = document.querySelector('[data-slq-claim-modal]');
        var btn = m2 && m2.querySelector('[data-slq-claim-apply]');
        if (btn) { btn.disabled = false; btn.textContent = 'Apply my new results'; }
        var el2 = m2 && m2.querySelector('[data-slq-claim-error]');
        if (el2) {
          el2.hidden = false;
          var code = String(err && err.message);
          el2.textContent = /EXPIRED/.test(code)
            ? 'These results have expired — take the quiz again for a fresh set.'
            : /LOGIN_REQUIRED/.test(code)
              ? 'Please log in to your account first, then apply your new results.'
              : /NOT_OWNER/.test(code)
                ? 'These results belong to a different email address than the account you are logged in to.'
                : 'We could not apply your results just now. Please try again.';
        }
        try { console.warn('[SLQ] claim failed:', err && err.message); } catch (e2) { /* noop */ }
      });
  }, true);

  // "Log in" inside the prompt: remember where to come back to. quiz.liquid
  // (legacy, still in the layout) redirects a freshly logged-in customer to
  // localStorage.checkout_url — the same hand-off the legacy quiz used — and
  // the anchor's own href carries return_url for good measure.
  document.addEventListener('click', function (ev) {
    var el = ev.target;
    while (el && el !== document) {
      if (el.hasAttribute && el.hasAttribute('data-slq-claim-login')) break;
      el = el.parentNode;
    }
    if (!el || el === document) return;
    var back = el.getAttribute('data-slq-claim-login') || (window.location.pathname + window.location.search);
    try { localStorage.setItem('checkout_url', back); } catch (e) { /* storage blocked */ }
  }, true);

  function slqUuid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) { /* noop */ }
    var s = ''; for (var i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16);
    return s.slice(0, 8) + '-' + s.slice(8, 12) + '-4' + s.slice(13, 16) + '-a' + s.slice(17, 20) + '-' + s.slice(20, 32);
  }

  /* Public: a LOGGED-IN customer has a pending re-quiz — ask before applying.
     opts: { nonce, skinType, practiceName, proxyBase, onApplied } */
  function offerStagedApply(opts) {
    if (!opts || !opts.nonce || document.querySelector('[data-slq-claim-modal]')) return false;
    var m = slqClaimModal(claimPromptHtml(opts.skinType ? String(opts.skinType) : '', 'login'));
    pendingClaim = {
      mode: 'login', nonce: String(opts.nonce), proxyBase: opts.proxyBase || '/apps/quiz',
      onApplied: opts.onApplied,
    };
    return !!m;
  }

  /* Public: a LOGGED-IN customer arrived with ?staged=<nonce> that is NOT
     theirs (the quiz was taken with a different email while this account was
     logged in, or someone opened another person's link). Shopify's signed
     staged-pending said "nothing for you"; the nonce-gated read tells us the
     row is still waiting. Say so, and offer the only fix: log out, then log
     in with the quiz email — the claim prompt is waiting on the way back.
     opts: { nonce, ec2Base, me } */
  function offerStagedOtherAccount(opts) {
    if (!opts || !opts.nonce || !opts.ec2Base) return false;
    if (!/^[a-f0-9]{24,64}$/i.test(String(opts.nonce))) return false;
    if (document.querySelector('[data-slq-claim-modal]')) return false;
    var base = String(opts.ec2Base).replace(/\/+$/, '');
    var back = window.location.pathname + '?staged=' + encodeURIComponent(opts.nonce);
    var me = opts.me ? String(opts.me) : '';
    fetch(base + '/quiz/staged-results/' + encodeURIComponent(opts.nonce))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (row) {
        if (!row || row.status !== 'awaiting_login') return; // applied/expired — nothing to say
        var type = row.skinType ? String(row.skinType) : '';
        var esc = function (s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); };
        slqClaimModal(
          '<div style="width:42px;height:42px;margin:0 auto;clip-path:polygon(50% 0%,67% 9%,85% 15%,91% 33%,100% 50%,91% 67%,85% 85%,67% 91%,50% 100%,33% 91%,15% 85%,9% 67%,0% 50%,9% 33%,15% 15%,33% 9%);background:var(--slq-type-color,#2F3572);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px">!</div>'
          + '<h3 style="font-family:var(--slq-head-font,\'Poppins\',sans-serif);font-weight:700;color:#2F3572;font-size:21px;margin-top:10px">These results belong to a different email</h3>'
          + '<p style="font-size:13px;color:#6E7076;margin-top:6px;line-height:1.45">The new result' + (type ? ' (<b style="color:#2F3572">' + esc(type) + '</b>)' : '') + ' was saved for the email typed on the quiz' + (me ? ', but you\u2019re logged in as <b style="color:#2F3572">' + esc(me) + '</b>' : '') + '. Log out, then log in with that email and we\u2019ll ask you to confirm before anything changes. The email we sent it works too.</p>'
          + '<a data-slq-claim-logout="' + esc(back) + '" href="/account/logout" style="display:block;width:100%;margin-top:12px;background:#2F3572;color:#fff;font-weight:600;font-size:14px;padding:13px 10px;border-radius:999px;border:none;cursor:pointer;font-family:inherit;line-height:1.25;text-decoration:none;box-sizing:border-box">Log out to switch accounts</a>'
          + '<button type="button" data-slq-claim-cancel style="width:100%;margin-top:8px;background:none;border:none;color:#6E7076;font-weight:600;font-size:13px;padding:9px;cursor:pointer;font-family:inherit;text-decoration:underline">Keep my current results</button>'
        );
      })
      .catch(function () { /* additive UI — never break the page */ });
    return true;
  }

  /* Public: a LOGGED-OUT visitor arrived with ?staged=<nonce> — the row is
     theirs to apply once they log in. Reads the row's type/status off EC2 (the
     nonce-gated read the claim flow already uses) and asks them to log in.
     opts: { nonce, ec2Base, resultsUrl } */
  function offerStagedLogin(opts) {
    if (!opts || !opts.nonce || !opts.ec2Base) return false;
    if (!/^[a-f0-9]{24,64}$/i.test(String(opts.nonce))) return false;
    if (document.querySelector('[data-slq-claim-modal]')) return false;
    var base = String(opts.ec2Base).replace(/\/+$/, '');
    var back = (opts.resultsUrl || window.location.pathname) + '?staged=' + encodeURIComponent(opts.nonce);
    fetch(base + '/quiz/staged-results/' + encodeURIComponent(opts.nonce))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (row) {
        if (!row || row.status !== 'awaiting_login') return; // applied/expired — nothing to ask
        var type = row.skinType ? String(row.skinType) : '';
        slqClaimModal(
          '<div style="width:42px;height:42px;margin:0 auto;clip-path:polygon(50% 0%,67% 9%,85% 15%,91% 33%,100% 50%,91% 67%,85% 85%,67% 91%,50% 100%,33% 91%,15% 85%,9% 67%,0% 50%,9% 33%,15% 15%,33% 9%);background:var(--slq-type-color,#2F3572);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px">&#10003;</div>'
          + '<h3 style="font-family:var(--slq-head-font,\'Poppins\',sans-serif);font-weight:700;color:#2F3572;font-size:21px;margin-top:10px">Your new results are ready</h3>'
          + '<p style="font-size:13px;color:#6E7076;margin-top:6px;line-height:1.45">Your new result' + (type ? ' (<b style="color:#2F3572">' + type + '</b>)' : '') + ' is waiting. Log in to your account and we\u2019ll ask you to confirm before anything changes.</p>'
          + '<a data-slq-claim-login="' + back.replace(/"/g, '&quot;') + '" href="/account/login?return_url=' + encodeURIComponent(back) + '" style="display:block;width:100%;margin-top:12px;background:#2F3572;color:#fff;font-weight:600;font-size:14px;padding:13px 10px;border-radius:999px;border:none;cursor:pointer;font-family:inherit;line-height:1.25;text-decoration:none;box-sizing:border-box">Log in to apply</a>'
          + '<button type="button" data-slq-claim-cancel style="width:100%;margin-top:8px;background:none;border:none;color:#6E7076;font-weight:600;font-size:13px;padding:9px;cursor:pointer;font-family:inherit;text-decoration:underline">Not now</button>'
        );
      })
      .catch(function () { /* additive UI — never break the page */ });
    return true;
  }

  function maybeStartClaimFlow() {
    var p = slqClaimParams();
    if (!p || !S.ec2Base) return false;
    var base = String(S.ec2Base).replace(/\/+$/, '');

    fetch(base + '/quiz/staged-results/' + p.nonce)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (row) {
        var type = row && row.skinType ? String(row.skinType) : '';
        var pending = row && row.status === 'awaiting_login';
        var m = slqClaimModal(
          pending
            ? claimPromptHtml(type, 'token')
            : '<div style="width:42px;height:42px;margin:0 auto;clip-path:polygon(50% 0%,67% 9%,85% 15%,91% 33%,100% 50%,91% 67%,85% 85%,67% 91%,50% 100%,33% 91%,15% 85%,9% 67%,0% 50%,9% 33%,15% 15%,33% 9%);background:var(--slq-type-color,#2F3572);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px">&#10003;</div>'
              + '<h3 style="font-family:var(--slq-head-font,\'Poppins\',sans-serif);font-weight:700;color:#2F3572;font-size:21px;margin-top:10px">You have new skin type results</h3>'
              + '<p style="font-size:13px;color:#6E7076;margin-top:6px;line-height:1.45">'
              + (row && row.status === 'committed'
                ? 'These results were already applied — you&rsquo;re all set.'
                : 'This confirmation link is no longer valid. If you still want to update your results, just take the quiz again.')
              + '</p>'
              + '<button type="button" data-slq-claim-cancel style="width:100%;margin-top:12px;background:#2F3572;color:#fff;font-weight:600;font-size:14px;padding:13px 10px;border-radius:999px;border:none;cursor:pointer;font-family:inherit">Close</button>'
        );
        // Buttons are handled by the document-level delegated listener above —
        // per-node listeners were being stripped by this page's lifecycle.
        pendingClaim = { mode: 'token', nonce: p.nonce, ct: p.ct, base: base };
      })
      .catch(function () { /* claim UI is additive — never break the page */ });
    return true;
  }

  function init(opts) {
    opts = opts || {};
    var root = opts.root || document.querySelector('[data-slq-results]');
    if (!root) {
      if (window.console) console.error('[SLQ.results] root [data-slq-results] not found');
      return null;
    }
    if (S) unbindWindow();
    var dom = cacheDom(root);
    var envEc2 = envEc2Override();
    if (envEc2) {
      try { console.warn('[SLQ.results] API base overridden by ?slqEnv -> ' + envEc2); } catch (e) { /* noop */ }
      showEnvBanner(envEc2);
    }
    S = {
      root: root,
      els: dom.els,
      tpls: dom.tpls,
      ec2Base: envEc2Override() || opts.ec2Base || '',
      sfDomain: opts.sfDomain || '',
      sfToken: opts.sfToken || '',
      country: opts.country || '',
      currency: opts.currency || '',
      token: opts.widgetToken || '',
      skinType: opts.skinType || '',
      sensitivity: opts.sensitivity || '',
      kit: { status: 'idle', product: null, step: null },
      status: 'idle',
      steps: [],
      swaps: {},
      removed: new Set(),
      size: 'core',
      tod: 'all',
      glanceTab: 'all',
      cardOpen: {},
      mobOpen: null,
      drawerKey: null,
      drawerFrom: null,
      flashKey: null,
      flashTimer: null,
      gridCap: null,
      sbVisible: false,
      sbOpen: window.innerWidth > 760,
      subs: new Map(),
      planCache: new Map(),
      /* Subscribe & Save default-on bookkeeping. These are deliberately TWO
         facts, because one flag doing both jobs shipped a consent bug: the
         "3-4 Step" default pass set it, so expanding to "5+ Step" never
         subscribed the newly-visible products — masterSubState then read
         "not all subscribed" and rendered the toggle OFF while the five
         already-subscribed products kept their recurring plans all the way
         into checkout. The visitor saw OFF and would have been billed monthly.

         subOptedOut       — the visitor deliberately chose one-time purchase.
                             Nothing may auto-subscribe after that.
         subConsidered     — variant ids the default pass has already offered a
                             subscription to. A unit is auto-subscribed at most
                             once, so turning a SINGLE product off by hand is
                             never undone, while genuinely new units (revealed
                             by 5+ Step, or by a swap) still get the default. */
      subOptedOut: false,
      subConsidered: new Set(),
      ingredientInfo: null,
      viewedFired: false,
      adding: false,
      onScroll: null,
      onResize: null
    };
    root.hidden = false;
    applyTheme();
    // The takeover keeps the page's scroll position when the view swaps, so
    // arriving from the quiz/calculating step drops the visitor part-way down
    // the results — showing blank space above the hero. Start at the top.
    try { window.scrollTo(0, 0); } catch (e) { /* noop */ }
    // static listeners bind once per root element (re-init reuses them via the global S)
    if (!root.__slqResultsBound) {
      bindStatic();
      root.__slqResultsBound = true;
    } else {
      S.onScroll = function () { updateSbVisibility(); };
      S.onResize = function () { updateSbVisibility(); scheduleRender(); reclampAllIngRows(); };
      window.addEventListener('scroll', S.onScroll, { passive: true });
      window.addEventListener('resize', S.onResize);
    }
    // An email-claim visit arrives with NO widget token; the claim flow, once
    // confirmed, supplies one and re-enters the normal fetch path.
    var claiming = maybeStartClaimFlow();
    if (!S.ec2Base || (!S.token && !claiming)) {
      // No token, but a visitor who saved their results still has a cached
      // copy — show that rather than telling them to retake a quiz they
      // already took.
      if (!S.token && renderFromCacheOnly()) {
        ensureKit();
        return NS.results;
      }
      S.status = 'error';
      showState('error');
      S.els['error-msg'].textContent = !S.token
        ? 'There are no results saved in this browser. Retake the quiz — or log in if you saved your results to an account.'
        : 'This results view is missing its API configuration.';
      showErrorActions(!S.token ? 'missing' : 'transient');
      fireResultsViewed(null);
      return NS.results;
    }
    // Resolve the kit alongside the regimen — it only needs skinType +
    // sensitivity — so the Essentials Kit tab's visibility is settled by the
    // time the results are readable, instead of on first click.
    ensureKit();
    fetchRegimen();
    return NS.results;
  }

  /* Handshake with the quiz engine (sts-liquid-quiz.js): on a successful
   * submit it calls SLQ.results.show(SLQ.state) — {widgetToken, skinType,
   * sensitivityLevel, ...}. The API bases live in SLQ.settings (read from the
   * section root's data attributes by the quiz engine). */
  function show(state) {
    state = state || {};
    var settings = NS.settings || {};
    return init({
      ec2Base: settings.ec2Base || '',
      widgetToken: state.widgetToken || '',
      skinType: state.skinType || '',
      sensitivity: state.sensitivityLevel || ''
    });
  }

  NS.results = {
    version: 'liquid-poc-1',
    init: init,
    show: show,
    /* Is there a usable saved copy for this visitor? Lets the page bootstrap
     * choose between init-from-cache and the honest empty state without
     * duplicating the freshness/skin-type rules. Safe before init(). */
    hasSavedCopy: function () { return !!readCachedRegimen(); },
    /* Storefront "log in -> Apply your new results" prompts (see the section
     * bootstrap in sts-liquid-results.liquid). Safe before init(). */
    offerStagedApply: offerStagedApply,
    offerStagedLogin: offerStagedLogin,
    offerStagedOtherAccount: offerStagedOtherAccount,
    /* pure helpers exposed for parity tests / debugging — same semantics as the widget's regimen.ts + step-groups.ts */
    _internal: {
      isAddableStep: isAddableStep,
      isInformationalStep: isInformationalStep,
      isBundleAddableStep: isBundleAddableStep,
      isValidSwapCandidate: isValidSwapCandidate,
      substituteOutOfStock: substituteOutOfStock,
      filterStepIndicesByTier: filterStepIndicesByTier,
      computeAddableTotal: computeAddableTotal,
      applySwap: applySwap,
      applySwaps: applySwaps,
      buildStepGroups: buildStepGroups,
      dedupeByVariant: dedupeByVariant,
      categoryForSlot: categoryForSlot,
      timeBadgeLabel: timeBadgeLabel,
      brighten: brighten,
      money: money
    }
  };

  /* If the quiz engine's success outcome landed before this file finished
   * loading, it stashed the state in SLQ.pendingResult — consume it now
   * (contract stated in sts-liquid-quiz.js finishSuccess). */
  if (NS.pendingResult) {
    var pendingState = NS.pendingResult;
    NS.pendingResult = null;
    show(pendingState);
  }
})();
